import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, CreditCard, DollarSign, Coins, Check, TrendingUp, Shield, Send, QrCode, Copy, Phone, ShoppingBag, Gift, Delete, ChevronDown, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCelebration } from './ui/CelebrationProvider';
import { buildReceipt } from '../shared/receipts';
import { CheckoutReceiptActions } from './checkout/CheckoutReceiptActions';
import aiManager from '../services/AIManagerService';
import aiSettings from '../services/AISettingsService';
import { sendMobilePaymentLink, isValidPhoneNumber, createPaymentQRCode } from '../services/mobilePaymentService';
import { analyzeDiscountRequest, evaluateCheckoutWindow, DEFAULT_FRAUD_CONFIG, type FraudDetectionConfig, type DiscountRequest, type DiscountReason, type FraudAnalysisResult } from '../services/fraudDetectionService';
import { getFraudConfig } from '../services/fraudSettingsService';
import { mapReasonToFraud, totalDiscountAmount, computeDiscountedPrice, type ServiceDiscount } from '../utils/discount';
import { computeServiceSubtotal } from '../utils/checkoutPricing';
import { slikSaved } from '../utils/savedBurst';
import discountApprovalService from '../services/discountApprovalService';
import { shopSettingsService } from '../services/shopSettingsService';
import { appointmentService } from '../services/appointmentService';
import { inventoryService } from '../services/inventoryService';
import { giftCardService, GiftCard } from '../services/giftCardService';
import { isTaxable, categoryOfItemType, applyCardToTotal, splitRedemption, isRedeemable } from '../shared/giftCard';
import aiManagerAPI, { type NudgeResponse } from '../services/AIManagerAPI';
import { smsService, buildCheckoutReceiptBody } from '../services/smsService';
import { communicationService } from '../services/communicationService';
import { staffService } from '../services/staffService';
import { clientDataService, SavedPaymentMethod } from '../services/clientDataService';
import { getWalletForClient, redeemCoins } from '../services/coinWalletService';
import { chargeCardOnFile } from '../services/stripePaymentService';
import { enqueueForCheckout } from '../services/reviewRequestService';
import { formatServiceName } from '../utils/formatters';
import { usePermission } from '../hooks/usePermission';
import { isTerminalAvailable, collectCardPayment, cancelReaderOperation, warmConnectReader } from '../services/terminalService';
import { db } from '../config/firebase';
import { collection, addDoc, Timestamp, doc, getDoc } from 'firebase/firestore';

// Flexible appointment type that works with both CalendarView and other components
interface CheckoutAppointment {
  id?: string;
  clientId?: string;
  clientName: string;
  clientPhone?: string;
  clientEmail?: string;
  serviceName?: string;  // For AppointmentData
  service?: string;      // For CalendarView Appointment
  duration?: number;
  price?: number;
  barberId?: string;
  barberName?: string;   // Barber display name
  additionalItems?: Array<{ type: 'service' | 'product' | 'giftcard', name: string, price: number, productId?: string, recipientName?: string, recipientEmail?: string, personalMessage?: string, deliveryMethod?: 'in-person' | 'email' | 'print', code?: string, barberId?: string, barberName?: string, linkedAppointmentId?: string }>;
  // Client intelligence for checkout display
  clientBadges?: {
    cpiTier?: 'VIP' | 'Loyal' | 'New';
    cpiScore?: number;
    loyaltyTier?: 'Platinum' | 'Gold' | 'Silver' | 'Bronze';
    memberSince?: string;
    totalSpent?: number;
    visitCount?: number;
  };
}

interface SmartCheckoutModalProps {
  appointment: CheckoutAppointment | null;
  isOpen: boolean;
  onClose: () => void;
  onPaymentComplete?: (transaction: any) => void;
  // When provided, the "Checkout complete!" celebration shows a top-right "Rebook" link (Jose
  // 2026-08-10) so staff can jump straight to rebooking without reopening the appointment card.
  onRebook?: () => void;
}

export const SmartCheckoutModal: React.FC<SmartCheckoutModalProps> = ({
  appointment,
  isOpen,
  onClose,
  onPaymentComplete,
  onRebook
}) => {
  const { celebrate } = useCelebration();
  // NO PRE-SELECTED METHOD (Jose 2026-10-19): nothing highlighted until the client TOUCHES a tender —
  // touching is what pops the tip (Credit) or tender (Cash) modal. Starts null.
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'mobile' | 'slik-coins' | 'prepaid' | null>(null);
  // GIFT-CARD REDEMPTION (2026-08-15): "Apply Gift Card" draws a card's balance down against this
  // ticket, reducing the amount due; the remainder is paid by the method above. Wires the
  // previously-DEAD redeemGiftCard. Redeemed $ are NOT new revenue (booked at sale) — see brain.
  const [gcRedeemCode, setGcRedeemCode] = useState('');
  const [gcRedeemCard, setGcRedeemCard] = useState<GiftCard | null>(null);
  const [gcRedeemError, setGcRedeemError] = useState('');
  // Offer-to-save a redeemed gift card to the client's account (Jose 2026-11-01) → shows as a payment
  // method on their appointment card. Set after a successful redemption; a small Yes/No prompt links it.
  const [pendingGcLink, setPendingGcLink] = useState<{ giftCardId: string; clientId: string; clientName: string } | null>(null);
  const [gcValidating, setGcValidating] = useState(false);
  // Gift cards SAVED to this client (Jose 2026-11-01) — shown in the Gift Card pay section as tap-to-apply,
  // so staff never retype the code for a card already on the account.
  const [clientGiftCards, setClientGiftCards] = useState<GiftCard[]>([]);
  useEffect(() => {
    if (!isOpen || !appointment?.clientId) { setClientGiftCards([]); return; }
    let cancelled = false;
    giftCardService.getActiveGiftCardsForClient(appointment.clientId)
      .then((gcs) => { if (!cancelled) setClientGiftCards(gcs); })
      .catch(() => { if (!cancelled) setClientGiftCards([]); });
    return () => { cancelled = true; };
  }, [isOpen, appointment?.clientId]);
  // One-tap: apply a saved card without typing its code (it's already validated/active from the loader).
  const applyLinkedGiftCard = (card: GiftCard) => {
    setGcRedeemCode(card.code); setGcRedeemError(''); setShowGiftCardPay(true); setGcRedeemCard(card);
  };
  // INLINE "add this card to this client" (Jose 2026-11-01) — right on the applied-card row, not a
  // post-checkout popup: stamp linkedClientId now so it shows as a saved payment method next time.
  const [gcLinkingInline, setGcLinkingInline] = useState(false);
  const linkAppliedCardToClient = async () => {
    if (!gcRedeemCard || !appointment?.clientId) return;
    setGcLinkingInline(true);
    try {
      await giftCardService.linkToClient(gcRedeemCard.id, appointment.clientId);
      setGcRedeemCard({ ...gcRedeemCard, linkedClientId: appointment.clientId } as GiftCard);
      giftCardService.getActiveGiftCardsForClient(appointment.clientId)
        .then((gcs) => setClientGiftCards(gcs)).catch(() => {});
      toast.success(`Saved to ${appointment.clientName || 'this client'} ✓`);
    } catch {
      toast.error('Could not save the card to the account');
    } finally {
      setGcLinkingInline(false);
    }
  };
  // Gift Card as a PAYMENT METHOD (2026-08-16): the "Gift Card" button (replaces Coins) reveals the
  // code entry; the card draws the ticket down and any remainder pays on the selected method.
  const [showGiftCardPay, setShowGiftCardPay] = useState(false);
  // Clear any applied gift card whenever the modal (re)opens for a checkout, so a card applied to one
  // sale can NEVER leak into the next one. Placed with the state (before the early return) so hook
  // order stays stable. (2026-08-15)
  useEffect(() => {
    if (isOpen) { setGcRedeemCard(null); setGcRedeemCode(''); setGcRedeemError(''); setShowGiftCardPay(false); setSplitCashInput(''); setShowSplit(false); setUseSlikCoins(false); setCoinBalance(0); setCoinAccountId(null); }
  }, [isOpen, appointment?.id]);
  // 🪙 Load the client's REAL coin balance when the checkout opens (clients/{uid}.loyaltyPoints, phoneKey
  // fallback for wrong-record imports). Was hardcoded 0 → coins never showed at the register. (Jose 2026-10-31)
  useEffect(() => {
    let alive = true;
    if (isOpen && appointment?.clientId) {
      const rec = clientDataService.getClientById(appointment.clientId);
      const phone = appointment.clientPhone || rec?.phone || '';
      getWalletForClient(appointment.clientId, phone)
        .then((w) => { if (alive) { setCoinBalance(w.balance); setCoinAccountId(w.accountId); } })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [isOpen, appointment?.clientId, appointment?.clientPhone]);
  const [tipPercentage, setTipPercentage] = useState(0); // default No Tip (Jose 2026-08-14)
  const [customTip, setCustomTip] = useState('');
  // Custom-tip popup (Jose 2026-08-10): replaces the number-spinner. Enter a custom $ OR % tip.
  const [showCustomTipModal, setShowCustomTipModal] = useState(false);
  const [customTipMode, setCustomTipMode] = useState<'$' | '%'>('$');
  const [customTipDraft, setCustomTipDraft] = useState('');
  // PER-BARBER TIP SPLIT (2026-08-17): on a multi-barber ticket the client can give each barber a
  // different tip via Custom. tipSplit holds explicit per-barber dollars (null = uniform preset).
  const [tipSplit, setTipSplit] = useState<Record<string, number> | null>(null);
  const [showTipSplitModal, setShowTipSplitModal] = useState(false);
  const [tipSplitDraft, setTipSplitDraft] = useState<Record<string, { mode: '$' | '%'; val: string }>>({});
  // Which barber's in-app keypad is open in the split modal (highlights that row; avoids the native keyboard).
  const [activeTipBarberId, setActiveTipBarberId] = useState<string | null>(null);
  const [useSlikCoins, setUseSlikCoins] = useState(false);
  // 🪙 SLIK COIN WALLET (Jose 2026-10-31): the REAL balance from clients/{uid}.loyaltyPoints (100 coins = $1).
  // The checkout used to hardcode this to 0, so coins never showed at the register. Loaded on open; redeemed
  // coins are debited from coinAccountId on completion. See coinWalletService.
  const [coinBalance, setCoinBalance] = useState(0);
  const [coinAccountId, setCoinAccountId] = useState<string | null>(null);
  // MULTI-TENDER SPLIT (Jose 2026-10-30): part cash + rest card — "put $20 on the card, I'll give you the
  // rest in cash" (or vice-versa). The cash the operator collects DRAWS DOWN the ticket exactly like a
  // gift-card redemption; the card reader then charges the remaining amountDue. The sale stores
  // `splitCashAmount` so reporting peels that cash off the card method (no new paymentMethod enum needed).
  const [splitCashInput, setSplitCashInput] = useState('');
  const [showSplit, setShowSplit] = useState(false);
  const [processing, setProcessing] = useState(false);
  // BIG CENTERED CHARGE OVERLAY (Jose 2026-10-19): the client is watching the iPad — the live Stripe
  // status must be front-and-center, not a corner toast they miss. onStatus + the card-on-file path write
  // here; the overlay renders whenever readerBusy || cofBusy. "" → default "Starting the charge…".
  const [readerStatusMsg, setReaderStatusMsg] = useState('');
  // CASH TENDER CALCULATOR (Jose 2026-10-19): on a cash sale, pop a big "what did they hand you?" pad
  // in front of the client → show change. Display-only (cash never touches Stripe; the recorded amount
  // is always amountDue). cashConfirmedRef lets the modal's Complete re-enter handlePayment past the gate.
  const [showCashTenderModal, setShowCashTenderModal] = useState(false);
  const [cashTenderDraft, setCashTenderDraft] = useState('');
  const cashConfirmedRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      cashConfirmedRef.current = false; setShowCashTenderModal(false); setChargeApproved(false);
      cancelRequestedRef.current = false;
      // WARM-CONNECT the reader the moment checkout opens (Jose 2026-10-23) so by the time the operator
      // hits Complete Payment it's already linked — no cold "Looking for the reader…" wait. Best-effort,
      // silent, fire-and-forget; collectCardPayment falls back to a cold connect if this didn't land.
      if (isTerminalAvailable()) { warmConnectReader().catch(() => {}); }
    }
  }, [isOpen]);
  // TIP-ON-CREDIT (Jose 2026-10-19): tapping Credit pops a BIG tip picker in the client's face. Reuses
  // the existing tip presets + math (setTipPercentage / setCustomTip); the inline tip row stays as a fallback.
  const [showCreditTipModal, setShowCreditTipModal] = useState(false);
  // TIP CELEBRATION (Jose 2026-10-19): a quick burst when the client picks a tip, escalating with the %
  // — small sparkles at the low end up to fireworks at the top. Pure fun; clears itself after ~1s.
  const [tipBurst, setTipBurst] = useState<{ tier: number; key: number } | null>(null);
  const fireTipBurst = (pct: number) => {
    if (!pct || pct <= 0) { setTipBurst(null); return; }
    const tier = pct <= 15 ? 1 : pct <= 18 ? 2 : pct <= 20 ? 3 : pct <= 25 ? 4 : 5;
    setTipBurst({ tier, key: Date.now() });
    window.setTimeout(() => setTipBurst((b) => (b && Date.now() - b.key >= 950 ? null : b)), 1000);
  };
  const [aiSettingsState, setAiSettingsState] = useState(aiSettings.getSettings());
  const [dynamicPricing, setDynamicPricing] = useState<any>(null);
  const [fraudAlert, setFraudAlert] = useState<any>(null);
  const [smartTipSuggestion, setSmartTipSuggestion] = useState<number | null>(null);
  const [aiNudge, setAiNudge] = useState<NudgeResponse | null>(null);

  // Mobile Payment Link state
  const [showMobilePaymentModal, setShowMobilePaymentModal] = useState(false);
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentLinkGenerated, setPaymentLinkGenerated] = useState<string | null>(null);
  const [paymentQRCode, setPaymentQRCode] = useState<string | null>(null);
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);

  // Discount & Fraud Detection state
  // Per-service discounts now flow in FROM the appointment card (Jose 2026-08-09); this modal displays
  // them read-only and still runs the fraud gate. discountAmount/Reason/Notes are derived from them.
  const [serviceDiscounts, setServiceDiscounts] = useState<ServiceDiscount[]>([]);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState<DiscountReason>('service_issue');
  const [discountNotes, setDiscountNotes] = useState('');
  // Who/when paid — the audit trail for an "Already paid (prepaid)" checkout (Jose 2026-10-26). Required
  // when the prepaid tender is chosen so we always know who collected the money and when (e.g. a parent).
  const [prepaidNote, setPrepaidNote] = useState('');
  const [fraudAnalysis, setFraudAnalysis] = useState<FraudAnalysisResult | null>(null);
  const [barberStrikes, setBarberStrikes] = useState<number>(0);
  const [discountReasons, setDiscountReasons] = useState<{ id: string; label: string; type?: 'comp' | 'percent' | 'fixed'; value?: number }[]>([]);
  const [selectedDiscountId, setSelectedDiscountId] = useState<string | null>(null);
  const [checkoutBlocked, setCheckoutBlocked] = useState(false); // Critical risk blocks checkout
  const [readerBusy, setReaderBusy] = useState(false); // M2 reader collecting a card (native only)
  // When the card is READ (reader emits "Processing…") — so we can HOLD the "Processing / remove your
  // card" screen a guaranteed minimum before flipping to Approved (Jose 2026-10-22: the beat was flashing
  // past, client never saw it). 0 = no card-read yet this charge.
  const processingSinceRef = useRef(0);
  // When the big "Approved ✓ · finishing up" overlay first showed (Jose 2026-10-23). Backgrounding the
  // sale write made it instant, so the overlay used to flash by — the "finishing up" wait it covered is
  // now ~0. We guarantee it stays on screen a readable beat before the "Checkout complete" modal.
  const chargeApprovedAtRef = useRef(0);
  // Operator hit "Cancel charge" on the big reader overlay (Jose 2026-10-23): abort the in-flight
  // reader collect/scan WITHOUT it reading as a decline. When set, the charge's failure handler resets
  // cleanly (no red "Payment not completed" overlay) and stays on the checkout screen so he can retry.
  const cancelRequestedRef = useRef(false);
  // Extra services the operator removed from the ticket right here in checkout (Jose 2026-10-23: the
  // add-to-cart line had no trashcan). Signature-keyed so it survives re-renders; excluded from the
  // display, the totals AND the recorded sale below.
  const [removedExtraSigs, setRemovedExtraSigs] = useState<Set<string>>(new Set());
  // ✅ Charge approved, sale still recording (Jose 2026-10-21): the reader/card-on-file cleared but the
  // Firestore sale write (~seconds) is still running. Keep a BIG "Payment Approved · finishing up" overlay
  // through this window so the client/barber sees success INSTANTLY — not dead air + a tiny corner toast,
  // then confetti 45s later. Cleared right before the checkout celebration takes over.
  const [chargeApproved, setChargeApproved] = useState(false);
  // Persistent, in-checkout result of the last card-reader attempt (Jose 2026-08-30): a payment
  // outcome MUST be visible in the app, not buried in Stripe logs. Stays on screen until the barber
  // acts (retry clears it). { kind:'error', message } for a decline/failure so it can't be missed.
  const [readerResult, setReaderResult] = useState<{ kind: 'error' | 'success'; message: string } | null>(null);

  // TIP PRESETS FROM SETTINGS (Jose 2026-09-06): the suggested tip % buttons are configured in
  // Settings → Payments → "Suggested Tip Percentages" (shopSettings.paymentSettings.tipSuggestions).
  // Loaded on open; falls back to the classic [15,18,20,25] if settings can't be read.
  const [tipPresets, setTipPresets] = useState<number[]>([15, 20, 30, 40]); // Jose 2026-10-19: 15/20/30/40 (was 15/18/20/25 — too close)
  // Product tax rate from Settings → Payments (shopSettings.paymentSettings.taxRate). 0 until loaded — a
  // hardcoded 9% was overcharging every product ticket vs the shop's real rate. BEHAVIOR-PIN: plausible-but-wrong audit.
  const [taxRatePct, setTaxRatePct] = useState<number>(0);

  // CARD ON FILE (Jose 2026-09-06): if the checked-out client has a saved Stripe card, staff can
  // charge it off-session at checkout (no reader tap needed). cardOnFile holds the client's Stripe
  // customer id + their DEFAULT saved card; chargeToCardOnFile is the sub-choice under the Card tile
  // (on = charge saved card · off = use the M2 reader). cofBusy gates the button while charging.
  const [cardOnFile, setCardOnFile] = useState<{ customerId: string; pm: SavedPaymentMethod } | null>(null);
  const [chargeToCardOnFile, setChargeToCardOnFile] = useState(true);
  const [cofBusy, setCofBusy] = useState(false);

  // ── EDITABLE CHECKOUT TILES (Jose 2026-08-30) ─────────────────────────────────────────
  // Tap the Service tile to change its price / add a manual discount (mirrors the existing
  // DiscountReason + fraud gate — one discount system), and tap the Client tile to look up a
  // saved client (autocomplete) or type a new walk-in name. Overrides are local and flow into
  // the sale write; a booked appointment's own client is only reassigned if you pick a new one.
  const [editingService, setEditingService] = useState(false);
  const [priceOverride, setPriceOverride] = useState<number | null>(null);
  const [priceInput, setPriceInput] = useState('');
  const [editingClient, setEditingClient] = useState(false);
  const [clientOverride, setClientOverride] = useState<{ id?: string; name: string; phone?: string; email?: string } | null>(null);
  const [clientQuery, setClientQuery] = useState('');

  // ── CHECKOUT-WINDOW ANTI-FRAUD (SLIK #3) ──────────────────────────────────────────────
  // Who is doing the checkout? Owner/Manager may complete a checkout outside the appointment
  // window; a barber cannot (past the window = manager-only + flag). See fraudDetectionService.
  const { isManager, isOwner, role, can } = usePermission();
  // Tunable fraud config (Settings → Checkout Security). Loaded on open; defaults until then.
  const [fraudConfig, setFraudConfig] = useState<FraudDetectionConfig>(DEFAULT_FRAUD_CONFIG);
  useEffect(() => { if (isOpen) getFraudConfig().then(setFraudConfig).catch(() => {}); }, [isOpen]);
  // The appointment's REAL scheduled window (start = date+time, end = start + duration). Walk-ins
  // created at checkout time land ≈ now → never "past window". Missing/bad date/time → nulls → gate
  // fails open (never blocks a legit checkout on bad data).
  const apptWindow = useMemo<{ start: Date | null; end: Date | null }>(() => {
    const d = (appointment as any)?.date; const t = (appointment as any)?.time;
    if (!d || !t) return { start: null, end: null };
    // Parse date + time at LOCAL time, 12h AND 24h safe. The old `new Date(`${d}T${t}:00`)` was Invalid
    // for a 12-hour time ("5:30 PM" → "...T5:30 PM:00") → the window became null and the late-checkout
    // fraud gate SILENTLY did nothing for every 12-hour-time appt (and only worked for 24h ones). Now it
    // computes the true window for all appts, so the gate is consistent. (Jose 2026-10-24)
    const [yy, mm, dd] = String(d).split('-').map((n) => parseInt(n, 10));
    const tm = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(String(t));
    if (!yy || !mm || !dd || !tm) return { start: null, end: null };
    let hh = parseInt(tm[1], 10); const mi = parseInt(tm[2], 10);
    const ap = (tm[3] || '').toUpperCase();
    if (ap === 'PM' && hh < 12) hh += 12;
    if (ap === 'AM' && hh === 12) hh = 0;
    const start = new Date(yy, mm - 1, dd, hh, mi, 0, 0);
    if (isNaN(start.getTime())) return { start: null, end: null };
    const end = new Date(start.getTime() + (((appointment as any)?.duration || 30) * 60000));
    return { start, end };
  }, [appointment]);

  // AI Settings listener
  useEffect(() => {
    const handleSettingsChange = () => {
      setAiSettingsState(aiSettings.getSettings());
    };
    aiSettings.on('settingsChanged', handleSettingsChange);
    return () => {
      aiSettings.off('settingsChanged', handleSettingsChange);
    };
  }, []);

  // Load discount reasons and barber strikes when modal opens
  useEffect(() => {
    if (!isOpen || !appointment) return;

    const loadDiscountData = async () => {
      // Load discount reasons from shop settings
      // Fallback reasons — used if the load fails OR returns empty (Jose 2026-10-23: "discounts empty
      // again"). An empty return must NOT leave a blank picker, so treat it exactly like a failure.
      const FALLBACK_REASONS = [
        { id: 'service_issue', label: 'Service Issue' },
        { id: 'unsatisfied_client', label: 'Unsatisfied Client' },
        { id: 'loyalty', label: 'Loyalty Reward' },
        { id: 'grandfather_pricing', label: 'Grandfather Pricing' },
        { id: 'other', label: 'Other' },
      ];
      try {
        const reasons = await shopSettingsService.getDiscountReasons();
        if (!reasons || reasons.length === 0) {
          console.warn('Discount reasons came back empty — using fallback defaults.');
          setDiscountReasons(FALLBACK_REASONS);
        } else {
          setDiscountReasons(reasons.map(r => ({ id: r.id, label: r.label, type: r.type, value: r.value })));
        }
      } catch (error) {
        console.error('Failed to load discount reasons:', error);
        setDiscountReasons(FALLBACK_REASONS);
      }

      // Load barber strikes
      if (appointment.barberId) {
        try {
          const strikes = await discountApprovalService.getBarberStrikes(appointment.barberId);
          setBarberStrikes(strikes.length);
        } catch (error) {
          console.error('Failed to load barber strikes:', error);
          setBarberStrikes(0);
        }
      }

      // 💸 Per-service discounts chosen in the appointment card flow in here (Jose 2026-08-09). Feed the
      // aggregate into the EXISTING discountAmount/Reason pipeline so the fraud gate keeps running.
      const sd = ((appointment as any).serviceDiscounts as ServiceDiscount[] | undefined) || [];
      setServiceDiscounts(sd);
      const totalDisc = totalDiscountAmount(sd);
      if (totalDisc > 0) {
        const primary = sd.find(d => d.discountAmount > 0) || sd[0];
        setDiscountAmount(String(totalDisc));
        setDiscountReason(mapReasonToFraud(primary.reasonId));
        setDiscountNotes(sd.filter(d => d.discountAmount > 0).map(d => `${d.serviceName}: ${d.reasonLabel}`).join('; '));
      } else {
        setDiscountAmount('');
      }
    };

    loadDiscountData();
  }, [isOpen, appointment]);

  // TIP PRESETS (Jose 2026-09-06): pull the suggested tip % from shop settings so the tip buttons
  // reflect Settings → Payments → "Suggested Tip Percentages" instead of a hardcoded list. Keeps the
  // default if the config is empty/unreadable, so checkout never renders zero tip options.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    shopSettingsService.getSettings()
      .then(s => {
        const tips = s?.paymentSettings?.tipSuggestions;
        if (!cancelled && Array.isArray(tips) && tips.length > 0) {
          setTipPresets(tips.filter(n => typeof n === 'number' && n > 0));
        }
        const tr = s?.paymentSettings?.taxRate;
        if (!cancelled && typeof tr === 'number' && tr >= 0) setTaxRatePct(tr);
      })
      .catch(() => { /* keep default presets */ });
    return () => { cancelled = true; };
  }, [isOpen]);

  // CARD ON FILE (Jose 2026-09-06): when a known client is being checked out, read their saved Stripe
  // card fresh from Firestore (money path — don't trust a stale in-memory cache). If they have a
  // stripeCustomerId + at least one saved method, expose the "charge card on file" path at checkout.
  useEffect(() => {
    const clientId = appointment?.clientId;
    if (!isOpen || !clientId) { setCardOnFile(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'clients', clientId));
        if (cancelled) return;
        if (!snap.exists()) { setCardOnFile(null); return; }
        const d = snap.data() as any;
        const methods: SavedPaymentMethod[] = Array.isArray(d.savedPaymentMethods) ? d.savedPaymentMethods : [];
        const custId: string | undefined = d.stripeCustomerId;
        if (!custId || methods.length === 0) { setCardOnFile(null); return; }
        const def = methods.find(m => m.isDefault) || methods[0];
        if (!def?.stripePaymentMethodId) { setCardOnFile(null); return; }
        setCardOnFile({ customerId: custId, pm: def });
        setChargeToCardOnFile(true); // default to the saved card when one exists (fastest for a known client)
      } catch {
        if (!cancelled) setCardOnFile(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, appointment?.clientId]);

  // Automatic fraud analysis when discount changes (NEW - per user requirement)
  // User: "I want the analysis to be done automatically and remove the button"
  useEffect(() => {
    if (!isOpen || !appointment) return;

    // Only run analysis if there's a discount amount
    if (!discountAmount || parseFloat(discountAmount) <= 0) {
      setFraudAnalysis(null);
      return;
    }

    const runAutoFraudAnalysis = () => {
      const serviceName = appointment.serviceName || appointment.service || 'Service';
      const baseServicePrice = appointment.price ?? 0;
      const isMultiService = serviceName.includes(' + ') && baseServicePrice === 0;
      const serviceItems = appointment.additionalItems?.filter(item => item.type === 'service') || [];
      const additionalItems = appointment.additionalItems?.filter(item => item.type !== 'service') || [];
      const serviceItemsTotal = serviceItems.reduce((sum, item) => sum + item.price, 0);
      const additionalItemsTotal = additionalItems.reduce((sum, item) => sum + item.price, 0);
      const displayServicePrice = isMultiService ? serviceItemsTotal : baseServicePrice;
      const extraServicesBillable = !isMultiService ? serviceItemsTotal : 0;
      const subtotal = displayServicePrice + extraServicesBillable + additionalItemsTotal;

      // Map local payment method to DiscountRequest PaymentMethod type
      let mappedPaymentMethod: 'cash' | 'card' | 'digital_coins' | 'digital_wallet' | 'mixed' | 'other';
      if (paymentMethod === 'slik-coins') {
        mappedPaymentMethod = 'digital_coins';
      } else if (paymentMethod === 'mobile') {
        mappedPaymentMethod = 'digital_wallet';
      } else if (paymentMethod === 'cash' || paymentMethod === 'card') {
        mappedPaymentMethod = paymentMethod;
      } else {
        mappedPaymentMethod = 'other';
      }

      // Fields shared across every per-service fraud check
      const baseReq = {
        barberId: appointment.barberId || 'unknown',
        barberName: appointment.barberName || 'Unknown Barber',
        appointmentId: appointment.id || `temp-${Date.now()}`,
        clientId: appointment.clientId || 'unknown',
        clientName: appointment.clientName,
        paymentMethod: mappedPaymentMethod,
        // Use the appointment's REAL scheduled window so the checkout-timing fraud check fires when a
        // checkout is completed long after the appointment ended. Fallback = legacy assumption (no timing risk).
        serviceStartTime: apptWindow.start || new Date(Date.now() - 60 * 60 * 1000),
        serviceEndTime: apptWindow.end || new Date(),
        requestTime: new Date(),
        barberStrikeCount: barberStrikes,
      };

      // 💸 PER-SERVICE fraud (Jose 2026-08-09): analyze EACH discounted service on its OWN
      // price + %, then merge to worst-case. An aggregate blend can bury an 80%-off single
      // service inside a low overall % and skate past the critical threshold. See utils/discount.ts.
      const discounted = serviceDiscounts.filter(d => d.discountAmount > 0);
      let results: { label: string | null; res: FraudAnalysisResult }[];

      if (discounted.length > 0) {
        results = discounted.map(d => {
          const req: DiscountRequest = {
            ...baseReq,
            originalPrice: d.originalPrice,
            discountedPrice: d.discountedPrice,
            discountAmount: d.discountAmount,
            discountPercent: d.originalPrice > 0 ? (d.discountAmount / d.originalPrice) * 100 : 0,
            discountReason: mapReasonToFraud(d.reasonId),
            reasonNotes: `${d.serviceName}: ${d.reasonLabel}`,
          };
          return { label: d.serviceName, res: analyzeDiscountRequest(req, fraudConfig) };
        });
      } else {
        // Fallback: no per-service breakdown → analyze the aggregate (legacy/manual path)
        const discount = parseFloat(discountAmount);
        const req: DiscountRequest = {
          ...baseReq,
          originalPrice: subtotal,
          discountedPrice: subtotal - discount,
          discountAmount: discount,
          discountPercent: subtotal > 0 ? (discount / subtotal) * 100 : 0,
          discountReason,
          reasonNotes: discountNotes,
        };
        results = [{ label: null, res: analyzeDiscountRequest(req, fraudConfig) }];
      }

      // Merge → the worst-case service drives severity/riskScore/recommendations (so the existing
      // block gate at handlePayment keeps working unchanged), but flags from EVERY flagged service
      // surface (service-prefixed) so the manager sees exactly which one tripped it.
      const worst = results.reduce((a, b) => (b.res.riskScore >= a.res.riskScore ? b : a));
      const mergedFlags = Array.from(new Set(
        results.flatMap(({ label, res }) => res.fraudFlags.map(f => (label ? `${label}: ${f}` : f)))
      ));
      setFraudAnalysis({
        ...worst.res,
        fraudFlags: mergedFlags,
        isSuspicious: results.some(r => r.res.isSuspicious),
      });
    };

    runAutoFraudAnalysis();
  }, [discountAmount, paymentMethod, discountReason, discountNotes, barberStrikes, isOpen, appointment, serviceDiscounts, fraudConfig]);

  // Calculate AI recommendations when modal opens
  useEffect(() => {
    if (!isOpen || !appointment) return;

    const serviceName = appointment.serviceName || appointment.service || 'Service';
    const basePrice = appointment.price ?? 0;

    // Calculate actual service price (handle multi-service appointments)
    const isMultiService = serviceName.includes(' + ') && basePrice === 0;
    const serviceItems = appointment.additionalItems?.filter(item => item.type === 'service') || [];
    const serviceItemsTotal = serviceItems.reduce((sum, item) => sum + item.price, 0);
    const actualServicePrice = isMultiService ? serviceItemsTotal : basePrice;

    // Dynamic Pricing Analysis
    if (aiSettingsState.enabled && aiSettingsState.modules.dynamicPricing) {
      const pricing = aiManager.predictDynamicPricing(serviceName, actualServicePrice, new Date());
      setDynamicPricing(pricing);
    }

    // Smart Tip Recommendation (based on service quality & time)
    if (aiSettingsState.enabled && aiSettingsState.modules.clientScoring) {
      // Suggest higher tip for premium services, VIP clients, peak times
      const hour = new Date().getHours();
      const isPeakTime = (hour >= 17 && hour <= 20) || (hour >= 11 && hour <= 13);
      const baseTip = actualServicePrice >= 50 ? 20 : 18; // Premium service = higher tip
      const suggestedTip = isPeakTime ? baseTip + 2 : baseTip;
      setSmartTipSuggestion(suggestedTip); // HINT ONLY — never auto-select. Default stays No Tip so a
      // gift-card / product-only sale (or any sale) never pre-charges a tip the customer didn't pick. (Jose 2026-08-14)
    }
  }, [isOpen, appointment, aiSettingsState]);

  // 👨‍👩‍👧 FAMILY GROUP at checkout (Jose 2026-10-25, 3b-lite): if this appt was booked as part of a family
  // group (shared groupId), surface the OTHER members so staff know to check everyone out (today = separate
  // tickets per person). The one-tap "roll the whole group onto this ticket" billing is a deferred money
  // change (needs Jose's verification across cash/card/link + barber credit + no-double-charge). INFO ONLY.
  // MUST be before the early return below (rules-of-hooks).
  const familyGroupMembers = useMemo(() => {
    const gid = (appointment as any)?.groupId;
    if (!gid || !appointment?.id) return [] as Array<{ name: string; service: string; status: string }>;
    try {
      return appointmentService.getAllAppointments()
        .filter((a: any) => a.groupId === gid && a.id !== appointment.id && a.status !== 'cancelled')
        .map((a: any) => ({ name: String(a.clientName || '').split(' ')[0] || 'Guest', service: a.service || a.serviceName || 'Service', status: a.status }));
    } catch { return []; }
  }, [appointment]);

  if (!isOpen || !appointment) return null;

  // Handle both serviceName (AppointmentData) and service (CalendarView Appointment)
  const serviceName = appointment.serviceName || appointment.service || 'Service';
  // No fallback - service prices are configurable. A manual price override (walk-in tile edit) wins.
  const origBasePrice = appointment.price ?? 0; // the appointment's OWN stored price (NOT the override)
  const baseServicePrice = priceOverride != null ? priceOverride : origBasePrice;

  // Effective customer for this sale — a chosen client (or typed walk-in) overrides the appointment's.
  // CLIENT-RECORD FALLBACK (Jose 2026-10-30, "no email at checkout but he has one in his profile"): when
  // the appointment's own phone/email are blank (imports, or an online booking that didn't capture email),
  // resolve them from the linked CLIENT record — so the receipt can be emailed/texted. Same doctrine as
  // self-check-in + reminders (fall back to the client record for sparse appt contact fields).
  const effClientId = clientOverride ? (clientOverride.id || '') : appointment.clientId;
  const _clientRec = (!clientOverride && appointment.clientId) ? clientDataService.getClientById(appointment.clientId) : undefined;
  const effClientName = clientOverride?.name || appointment.clientName;
  const effClientPhone = clientOverride ? clientOverride.phone : (appointment.clientPhone || _clientRec?.phone || undefined);
  const effClientEmail = clientOverride ? clientOverride.email : ((appointment.clientEmail || '').trim() || _clientRec?.email || undefined);

  // Separate service items from truly additional items — minus anything the operator trashed in checkout.
  const extraSig = (i: any) => `${i.type}|${i.name}|${i.price}|${i.barberId || ''}`;
  const liveExtras = (appointment.additionalItems || []).filter((i: any) => !removedExtraSigs.has(extraSig(i)));
  const serviceItems = liveExtras.filter(item => item.type === 'service');
  const additionalItems = liveExtras.filter(item => item.type !== 'service');
  // Trash an added extra service line (instant locally; persist the removal to the appointment in the bg).
  const removeExtraService = (item: any) => {
    const s = extraSig(item);
    setRemovedExtraSigs(prev => { const n = new Set(prev); n.add(s); return n; });
    if (appointment.id && (appointment.additionalItems || []).some((i: any) => extraSig(i) === s)) {
      const next = (appointment.additionalItems || []).filter((i: any) => extraSig(i) !== s);
      appointmentService.updateAppointment(appointment.id, { additionalItems: next } as any, { background: true }).catch(() => {});
    }
    slikSaved('Removed');
  };

  // Calculate totals
  const serviceItemsTotal = serviceItems.reduce((sum, item) => sum + item.price, 0);
  const additionalItemsTotal = additionalItems.reduce((sum, item) => sum + item.price, 0);

  // Service subtotal via the ONE tested helper (src/utils/checkoutPricing.ts) so a combined "A + B" ticket
  // can NEVER double-charge: a manual override REPLACES the service subtotal (never adds), and for a
  // combined ticket the line items ARE the service (never re-billed). Detection is by NAME, never
  // `price === 0` — that heuristic broke on imports (total stored in price) + overrides. (Jose 2026-10-21)
  // extraServicesBillable: for a SINGLE-primary ticket the serviceItems are genuine extras credited to
  // their barber at completion (a linked/added service); for a combined ticket it is 0.
  const { isMultiService, displayServicePrice, extraServicesBillable } = computeServiceSubtotal({
    serviceName, origBasePrice, priceOverride,
    serviceItemsTotal, serviceItemCount: serviceItems.length,
  });

  // Subtotal includes all items
  const subtotal = displayServicePrice + extraServicesBillable + additionalItemsTotal;

  const taxRate = (taxRatePct || 0) / 100; // live shop rate from Settings → Payments (was a hardcoded 9% overcharge)
  const manualDiscount = discountAmount ? parseFloat(discountAmount) : 0;
  const subtotalAfterDiscount = subtotal - manualDiscount;
  // TAX MODEL (Jose 2026-08-14): ONLY PRODUCTS are taxed (9% sales tax). SERVICES are NOT taxed.
  // GIFT CARDS are NEVER taxed — not at purchase, not ever (a product later BOUGHT with a gift card
  // still gets product tax, but that happens on the redeem side). Tip excludes gift cards; default No Tip.
  // Split the non-service lines by category — the shared brain is the ONE authority. Gift cards
  // render in their OWN section (never under Products) and are never taxed; products are taxed.
  const productLines = additionalItems.filter((i: any) => categoryOfItemType(i.type) === 'product');
  const giftCardLinesDisplay = additionalItems.filter((i: any) => categoryOfItemType(i.type) === 'gift-card');
  const giftCardTotal = giftCardLinesDisplay.reduce((s, i) => s + i.price, 0);
  const productTaxable = additionalItems
    .filter((i: any) => isTaxable(categoryOfItemType(i.type)))
    .reduce((s, i) => s + i.price, 0);
  const tipBase = Math.max(0, subtotalAfterDiscount - giftCardTotal);

  // ── PER-BARBER TIP SPLIT (2026-08-17) ─────────────────────────────────────────────────────────
  // Barbers who performed a SERVICE on this ticket (product-only sellers are excluded — tips follow
  // service). Photo comes from the same staff record the appointment cards use.
  const cachedStaff = staffService.getCachedStaff();
  const barberPhoto = (bId?: string) => (bId ? cachedStaff.find(s => s.id === bId)?.photo : undefined);
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const svcBarberAgg = new Map<string, { barberId: string; name: string; serviceTotal: number }>();
  const addSvcBarber = (bId?: string, name?: string, amount?: number) => {
    if (!bId || !amount || amount <= 0) return;
    const cur = svcBarberAgg.get(bId) || { barberId: bId, name: name || 'Barber', serviceTotal: 0 };
    cur.serviceTotal += amount;
    if (name) cur.name = name;
    svcBarberAgg.set(bId, cur);
  };
  if (isMultiService) {
    // displayServicePrice = the SUM of serviceItems (the appointment's own components + any LINKED /
    // added services). Attribute each to its owner so a linked appointment by ANOTHER barber makes this
    // a split ticket: components carry no barberId → the ticket's barber; a linked appointment carries
    // its own barberId. (Adding displayServicePrice to the primary here would hide the split + over-credit.)
    serviceItems.forEach((s: any) => addSvcBarber(s.barberId || appointment.barberId, s.barberName || appointment.barberName, s.price));
  } else {
    // Primary service is separate (baseServicePrice); serviceItems are genuine extras — credit each to its barber.
    addSvcBarber(appointment.barberId, appointment.barberName, displayServicePrice);
    serviceItems.forEach((s: any) => addSvcBarber(s.barberId || appointment.barberId, s.barberName || appointment.barberName, s.price));
  }
  const serviceBarbers = Array.from(svcBarberAgg.values());
  const serviceTotalAll = serviceBarbers.reduce((sum, b) => sum + b.serviceTotal, 0);
  const isSplitTicket = serviceBarbers.length >= 2;

  // On a split ticket the tip is figured on SERVICE value only, so a preset % = "that % for each
  // barber" and product-only sellers get nothing. Single-barber tickets keep the existing base.
  const tipBaseForCalc = isSplitTicket ? serviceTotalAll : tipBase;
  const uniformTip = customTip ? parseFloat(customTip) : (tipBaseForCalc * tipPercentage) / 100;
  const tipAmount = (isSplitTicket && tipSplit)
    ? round2(Object.values(tipSplit).reduce((s, v) => s + (v || 0), 0))
    : uniformTip;
  // Per-barber allocation (only meaningful on a split ticket).
  const tipByBarber: Record<string, number> = {};
  if (isSplitTicket) {
    if (tipSplit) {
      serviceBarbers.forEach(b => { tipByBarber[b.barberId] = round2(tipSplit[b.barberId] || 0); });
    } else {
      let allocated = 0;
      serviceBarbers.forEach((b, i) => {
        let share: number;
        if (i === serviceBarbers.length - 1) { share = round2(tipAmount - allocated); }
        else { share = round2(tipAmount * (serviceTotalAll > 0 ? b.serviceTotal / serviceTotalAll : 0)); allocated += share; }
        tipByBarber[b.barberId] = share;
      });
    }
  }
  const tax = productTaxable * taxRate;
  const slikCoinsAvailable = coinBalance; // REAL balance from clients/{uid}.loyaltyPoints (Jose 2026-10-31)
  // 100 coins = $1 (engagementConfig.coinValueCents), capped at 50% of the subtotal; round to whole cents.
  const slikCoinDiscount = useSlikCoins ? Math.round(Math.min(slikCoinsAvailable / 100, subtotalAfterDiscount * 0.5) * 100) / 100 : 0;
  const total = subtotalAfterDiscount + tipAmount + tax - slikCoinDiscount;
  // A redeemed gift card draws down up to its balance against the final total; the rest is amountDue.
  const giftCardRedeemApplied = gcRedeemCard ? applyCardToTotal(total, gcRedeemCard.currentBalance).fromCard : 0;
  // SPLIT CASH (Jose 2026-10-30): a cash portion the operator collects draws down the ticket just like a
  // gift card, leaving the remainder for the card reader. Capped so it never exceeds what's left after the
  // gift card (if cash covers everything, amountDue → 0 and it books as a plain cash sale, not a comp).
  const splitCashApplied = Math.round(Math.min(Math.max(0, parseFloat(splitCashInput) || 0), Math.max(0, total - giftCardRedeemApplied)) * 100) / 100;
  const amountDue = Math.max(0, Math.round((total - giftCardRedeemApplied - splitCashApplied) * 100) / 100);
  const fullyCoveredByGiftCard = !!gcRedeemCard && amountDue === 0 && splitCashApplied === 0;
  // FULLY COMPED (Jose 2026-10-26): a 100% discount (Complimentary / Service Issue / Compensation) OR any
  // combination that zeroes the ticket means there's NOTHING to tender. The checkout must COMPLETE as a $0
  // sale without forcing a Cash/Credit pick — it was wrongly blocking with "Tap a payment method first."
  const fullyComped = amountDue === 0 && !fullyCoveredByGiftCard && splitCashApplied === 0;
  // PREPAID / ALREADY PAID (Jose 2026-10-26): the client already paid for THIS visit earlier (e.g. a
  // parent prepaid last week for the kid's cut today). The ticket keeps its FULL price (so the barber's
  // commission + revenue count), records paymentMethod 'prepaid', and charges NOTHING now (amountCharged 0,
  // nothing hits the till/card today). NOT a discount — the money was collected, just not at this terminal.
  const isPrepaid = paymentMethod === 'prepaid';

  // Custom-tip keypad helpers (Jose 2026-08-10). "%" mode has no decimal; on Add, a % is baked to $
  // (checkout subtotal is fixed) so it flows through the same customTip → tipAmount path.
  const appendTipDigit = (d: string) => {
    setCustomTipDraft(prev => {
      if (d === '.' && (prev.includes('.') || customTipMode === '%')) return prev;
      const next = (prev === '0' && d !== '.') ? d : prev + d;
      return next.length <= 6 ? next : prev;
    });
  };
  const applyCustomTip = () => {
    const val = parseFloat(customTipDraft) || 0;
    const dollars = customTipMode === '%' ? (subtotal * val) / 100 : val;
    setTipPercentage(0);
    setCustomTip(dollars > 0 ? dollars.toFixed(2) : '');
    setShowCustomTipModal(false);
  };
  const slikCoinsEarned = Math.floor(total);

  // tipPresets is state now — loaded from shop settings on open (see effect below).

  // Send SMS alerts to all managers/owners when a flagged discount is detected
  const sendManagerAlerts = (analysis: FraudAnalysisResult, severity: string) => {
    if (!smsService.isConfigured()) return;

    // Get managers and owners from staff service
    const managers = staffService.getStaffByRole('manager');
    const owners = staffService.getStaffByRole('owner');
    const recipients = severity === 'critical' ? [...managers, ...owners] : managers.length > 0 ? managers : owners;

    for (const staff of recipients) {
      if (!staff.phone) continue;
      smsService.sendManagerFraudAlert({
        managerPhone: staff.phone,
        managerName: `${staff.firstName} ${staff.lastName}`,
        barberName: appointment?.barberName || 'Unknown',
        clientName: appointment?.clientName || 'Unknown',
        originalPrice: subtotal + manualDiscount,
        discountAmount: manualDiscount,
        finalPrice: subtotal,
        paymentMethod: paymentMethod || 'unknown',
        riskScore: analysis.riskScore,
        severity,
        fraudFlags: analysis.fraudFlags,
        discountReason,
      }).catch(err => console.error(`Failed to alert ${staff.firstName}:`, err));
    }
  };

  // Validate a gift-card code for redemption — same guards as the write path (isRedeemable),
  // so a card the UI accepts is actually redeemable (no drift with redeemGiftCard).
  const validateRedeemCard = async () => {
    const code = gcRedeemCode.trim().toUpperCase();
    if (!code) return;
    setGcValidating(true);
    setGcRedeemError('');
    try {
      const card = await giftCardService.getGiftCardByCode(code);
      if (!card) {
        setGcRedeemError('Gift card not found');
      } else if (!isRedeemable(card)) {
        setGcRedeemError(
          card.status !== 'active'
            ? `Card is ${card.status}`
            : card.currentBalance <= 0
              ? 'Card has no balance left'
              : 'Card has expired',
        );
      } else {
        setGcRedeemCard(card);
      }
    } catch (e) {
      console.error('[Checkout] gift-card lookup failed:', e);
      setGcRedeemError('Could not look up that card — try again');
    } finally {
      setGcValidating(false);
    }
  };

  const handlePayment = async () => {
    // Must pick a tender first (no default anymore) — UNLESS the ticket is already $0 (fully comped or
    // fully covered by a gift card), in which case there is nothing to tender and we complete directly.
    if (!paymentMethod && amountDue > 0) { toast.error('Tap a payment method first (Cash or Credit).'); return; }
    // PREPAID AUDIT TRAIL (Jose 2026-10-26): "already paid" must capture WHO paid and WHEN, or the record
    // is unprovable later. Require a short note before completing a prepaid checkout.
    if (isPrepaid && !prepaidNote.trim()) { toast.error("Add who paid & when (e.g. “Dad paid cash Oct 19”)."); return; }
    // CASH TENDER GATE (Jose 2026-10-19): a cash sale first pops the big tender pad (enter cash given →
    // show change) in the client's face, THEN completes. The modal's Complete sets cashConfirmedRef and
    // re-calls handlePayment, which skips this gate. Non-cash tenders are unaffected.
    if (paymentMethod === 'cash' && !cashConfirmedRef.current && amountDue > 0) {
      setCashTenderDraft('');
      setShowCashTenderModal(true);
      return;
    }
    // ── SLIK #3: CHECKOUT-WINDOW ANTI-FRAUD GATE ──
    // A checkout completed far outside the appointment window is a fraud signal (back-dated / off-hours /
    // next-day close). Past the window beyond the manager threshold, only an Owner/Manager may complete it —
    // a barber is blocked and the attempt is flagged. Managers who complete a late checkout are logged too
    // (trap / trace / document). Walk-ins (checkout ≈ now) and bad/missing times never trip this (fails open).
    const windowVerdict = evaluateCheckoutWindow(apptWindow.end, new Date(), fraudConfig);
    // Authority brain (Jose 2026-10-25): owner ⊇ manager, PLUS a barber the owner granted the
    // `lateCheckout` trust capability — both sail past the late-checkout gate.
    const mayLateCheckout = isManager || can('lateCheckout');
    if (windowVerdict.requiresManager && !mayLateCheckout) {
      setProcessing(false);
      const mins = Math.round(windowVerdict.minutesLate);
      toast.error(
        `🚫 This appointment ended ~${mins} min ago — a manager must approve a checkout this far past the appointment window.`,
        { duration: 7000 },
      );
      try {
        await addDoc(collection(db, 'fraudAlerts'), {
          type: 'late_checkout_window',
          severity: 'high',
          barberId: appointment?.barberId || '',
          barberName: appointment?.barberName || '',
          appointmentId: appointment?.id || '',
          clientName: appointment?.clientName || '',
          minutesLate: mins,
          scheduledEnd: apptWindow.end ? apptWindow.end.toISOString() : null,
          attemptedByRole: role,
          blocked: true,
          attemptedAt: Timestamp.now(),
        });
      } catch (e) { console.error('late-checkout flag failed', e); }
      return;
    }
    if (windowVerdict.requiresManager && mayLateCheckout) {
      // Owner/Manager (or a lateCheckout-trusted barber) completing a late checkout — allowed, but logged.
      try {
        await addDoc(collection(db, 'fraudAlerts'), {
          type: 'late_checkout_manager_override',
          severity: 'medium',
          barberId: appointment?.barberId || '',
          barberName: appointment?.barberName || '',
          appointmentId: appointment?.id || '',
          clientName: appointment?.clientName || '',
          minutesLate: Math.round(windowVerdict.minutesLate),
          scheduledEnd: apptWindow.end ? apptWindow.end.toISOString() : null,
          overriddenByRole: role,
          blocked: false,
          attemptedAt: Timestamp.now(),
        });
      } catch (e) { console.error('late-checkout override log failed', e); }
    }

    // ── TIER 2: CRITICAL RISK = BLOCK CHECKOUT ──
    // Barber with strikes + large cash discount = stop them before money walks out
    if (fraudAnalysis && fraudAnalysis.severity === 'critical' && manualDiscount > 0) {
      setCheckoutBlocked(true);
      setProcessing(false);
      toast.error('🚫 Checkout blocked — manager approval required for this discount.', { duration: 6000 });

      // Create approval request in Firebase for manager review
      if (appointment?.barberId) {
        try {
          await discountApprovalService.createApprovalRequest(
            appointment.barberId,
            appointment.barberName || 'Unknown',
            {
              appointmentId: appointment.id || '',
              clientId: appointment.clientId || '',
              barberId: appointment.barberId,
              barberName: appointment.barberName || 'Unknown',
              clientName: appointment.clientName || 'Unknown',
              originalPrice: subtotal + manualDiscount,
              discountedPrice: subtotal,
              discountAmount: manualDiscount,
              discountPercent: Math.round((manualDiscount / (subtotal + manualDiscount)) * 100),
              discountReason,
              reasonNotes: discountNotes,
              paymentMethod: (paymentMethod === 'slik-coins' ? 'digital_coins' : paymentMethod === 'mobile' ? 'other' : paymentMethod) as 'cash' | 'card' | 'digital_coins' | 'other',
              serviceStartTime: apptWindow.start || new Date(Date.now() - 60 * 60 * 1000),
              serviceEndTime: apptWindow.end || new Date(),
              requestTime: new Date(),
              barberStrikeCount: barberStrikes
            },
            fraudAnalysis
          );
        } catch (err) {
          console.error('Failed to create approval request:', err);
        }

        // Send SMS to managers/owners
        sendManagerAlerts(fraudAnalysis, 'critical');
      }
      return;
    }

    setProcessing(true);
    setChargeApproved(false); // fresh attempt — no "approved" overlay until a charge actually clears
    chargeApprovedAtRef.current = 0; // fresh attempt — only a real approval re-stamps it

    const transaction = {
      id: `txn-${Date.now()}`,
      appointmentId: appointment.id,
      clientName: appointment.clientName,
      serviceName: serviceName,
      servicePrice: displayServicePrice,
      tip: tipAmount,
      tax,
      manualDiscount,
      manualDiscountReason: manualDiscount > 0 ? discountReason : undefined,
      manualDiscountNotes: manualDiscount > 0 && discountNotes ? discountNotes : undefined,
      fraudAnalysis: fraudAnalysis || undefined,
      slikCoinDiscount,
      total,
      paymentMethod,
      slikCoinsEarned,
      slikCoinsUsed: useSlikCoins ? Math.round(slikCoinDiscount * 100) : 0, // coins debited = $discount × 100
      timestamp: new Date().toISOString()
    };

    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 1500));

    // NOTE: A legacy POST to /api/payments/record-transaction lived here. This is a
    // frontend-only app (no such Cloud Function) — the POST hit the SPA index.html
    // rewrite and silently no-op'd (and stalled) on every checkout. Removed. Reporting
    // and payroll read the completed appointment (barberId/price/productsSold/tipAmount)
    // written below via reportingDataService.

    // 💰 FIRESTORE INTEGRATION: record the sale for reporting/payroll/inventory.
    // TWO paths, ONE money model (Jose 2026-08-13, register unify):
    //   • appointment.id present → this is a booked appointment being checked out → UPDATE it.
    //   • no id → a REGISTER walk-in / product / gift-card sale → CREATE a completed appointment so
    //     reporting, payroll and inventory treat it identically (barberId keeps the credit).
    // Hoisted out of the try so the checkout-complete card (rendered after the try/catch) can
    // reference the sale record id for the Refund/Void action (Jose 2026-08-19).
    let saleApptId: string | undefined = appointment.id;
    try {
      // If a gift card covered the WHOLE ticket, the payment method IS gift_card; otherwise the
      // method above pays the remainder (a partial redemption is recorded as its own draw-down).
      const aptPaymentMethod = fullyCoveredByGiftCard
        ? 'gift_card'
        : isPrepaid
          ? 'prepaid'   // already paid earlier (e.g. parent prepaid for the kid's cut) — full price, $0 charged now
          : fullyComped
            ? 'comp'   // $0 ticket from a 100% discount — record it as a comp, not a cash/card sale
            : (splitCashApplied > 0 && amountDue === 0)
              ? 'cash'   // SPLIT whose cash portion ended up covering the whole ticket → a plain cash sale
              : (paymentMethod === 'slik-coins' ? 'store_credit' : paymentMethod);

      // Resolve each sold product to its REAL inventory doc — by the id carried from the picker,
      // else by exact name — so the sale can deduct stock + credit the barber, and reporting can
      // cross-join productsSold back to inventory.
      const inventoryProducts = inventoryService.getAllProducts();
      // PER-BARBER CREDIT (2026-08-16): each product carries the barber who SOLD it (from the
      // Register's Barber-Selector); untagged products fall back to the ticket's barber. This is what
      // lets a product sold by another barber credit THEM, not the ticket owner. See split below.
      const primaryBarberId = appointment.barberId || 'unknown';
      const primaryBarberName = appointment.barberName || 'Unknown';
      // TIP SPLIT allocation (2026-08-17): on a multi-barber ticket each barber's tip rides onto their
      // OWN completed record. takeTip() hands a barber their share exactly once (first record wins), so
      // a barber with multiple lines isn't double-tipped. Single-barber tickets ignore this (the whole
      // tip stays on the primary record, unchanged).
      const tipRemaining: Record<string, number> = isSplitTicket ? { ...tipByBarber } : {};
      const takeTip = (bId: string) => { const v = tipRemaining[bId] || 0; tipRemaining[bId] = 0; return round2(v); };
      const productsSold = additionalItems
        .filter(item => item.type === 'product')
        .map(item => {
          const match = inventoryProducts.find(p => p.id === item.productId || p.name === item.name);
          return {
            productId: match?.id || `product-${item.name.toLowerCase().replace(/\s+/g, '-')}`,
            productName: item.name,
            quantity: 1,
            unitPrice: item.price,
            total: item.price,
            barberId: (item as any).barberId || primaryBarberId,
            barberName: (item as any).barberName || primaryBarberName,
          };
        });
      // Split products by the barber who sold them: the ticket owner keeps theirs on the primary
      // record; each OTHER barber gets their own completed record so reporting/payroll credit them.
      const primaryProducts = productsSold.filter(p => p.barberId === primaryBarberId);
      const otherBarberProducts = productsSold.filter(p => p.barberId !== primaryBarberId);

      // CARD ON FILE (Jose 2026-09-06): known client with a saved Stripe card + Card selected +
      // "charge card on file" chosen + online + amount due → charge the saved card off-session BEFORE
      // recording the sale. On success we record it as a real, refundable Stripe charge (paidOnline);
      // on any non-succeeded status or error we abort (no sale) — identical safety to the reader path.
      // Takes precedence over the M2 reader (a saved-card charge means no tap needed).
      let cardOnFilePI: string | null = null;
      const chargeSavedCard =
        paymentMethod === 'card' &&
        chargeToCardOnFile &&
        !!cardOnFile &&
        (typeof navigator === 'undefined' || navigator.onLine !== false) &&
        !fullyCoveredByGiftCard &&
        amountDue > 0;
      if (chargeSavedCard && cardOnFile) {
        setCofBusy(true);
        setReaderResult(null); // clear any prior decline banner on a fresh attempt
        setReaderStatusMsg(`Processing card on file •••• ${cardOnFile.pm.last4}…`); // BIG overlay (not a corner toast)
        const res = await chargeCardOnFile({
          amount: amountDue,
          customerId: cardOnFile.customerId,
          paymentMethodId: cardOnFile.pm.stripePaymentMethodId,
          description: `SLIK Barber Lounge — ${effClientName || 'Client'}`,
          receiptEmail: effClientEmail,
          metadata: { appointmentId: appointment.id || '', clientId: appointment.clientId || '' },
        });
        setCofBusy(false);
        toast.dismiss('cardonfile');
        if (!res.success) {
          const reason = res.error || 'Card on file could not be charged — try the reader or another tender.';
          setReaderResult({ kind: 'error', message: reason });
          toast.error(reason, { duration: 6000 });
          setProcessing(false);
          return;
        }
        cardOnFilePI = res.paymentIntentId || null;
        setReaderResult({ kind: 'success', message: `Charged card on file •••• ${cardOnFile.pm.last4} — $${amountDue.toFixed(2)} ✓` });
        toast.success(`Charged card on file •••• ${cardOnFile.pm.last4} ✓`);
        chargeApprovedAtRef.current = Date.now();
        setChargeApproved(true); // card cleared → show the big "Approved · finishing up" overlay now
      }

      // NATIVE M2 READER CHARGE (Jose 2026-08-30): online + native app + Card + amount due →
      // collect on the physical Stripe Terminal (M2) BEFORE recording the sale. On success we
      // record it as a real, refundable Stripe card_present charge. On failure we abort (no sale).
      // Only engages in the native app (isTerminalAvailable) — web/offline paths are untouched.
      // Skipped when the saved card already paid (chargeSavedCard) so a client is never double-charged.
      let terminalPaidPI: string | null = null;
      const chargeOnReader =
        paymentMethod === 'card' &&
        !chargeSavedCard &&
        (typeof navigator === 'undefined' || navigator.onLine !== false) &&
        isTerminalAvailable() &&
        !fullyCoveredByGiftCard &&
        amountDue > 0;
      if (chargeOnReader) {
        setReaderBusy(true);
        setReaderResult(null); // clear any prior decline banner on a fresh attempt
        cancelRequestedRef.current = false; // fresh attempt — a prior cancel must not suppress a real decline
        processingSinceRef.current = 0; // fresh charge — no card-read yet
        setReaderStatusMsg('Looking for the reader — please wait…'); // LOOKING phase copy (no "charge/process/tap" → no premature Processing/tap prompt)
        const res = await collectCardPayment(amountDue, {
          appointmentId: appointment.id || '',
          clientEmail: effClientEmail,
          description: `SLIK Barber Lounge — ${effClientName || 'Walk-in'}`,
          // live status → big overlay. Stamp the moment the card is READ (reader emits "Processing…") so
          // we can guarantee the "remove your card" beat is actually SEEN before Approved. (Jose 2026-10-22)
          onStatus: (m) => { setReaderStatusMsg(m); if (/processing/i.test(m) && !processingSinceRef.current) processingSinceRef.current = Date.now(); },
        });
        if (!res.success) {
          setReaderBusy(false);
          setReaderStatusMsg('');
          // Operator cancelled the charge from the overlay — NOT a decline. Reset quietly, no red
          // "Payment not completed" overlay or error toast; stay on the checkout screen to retry.
          if (cancelRequestedRef.current) {
            cancelRequestedRef.current = false;
            setReaderResult(null);
            setProcessing(false);
            return;
          }
          // Surface the EXACT reason in a persistent banner (not just a 4s toast) — the barber
          // needs to know a card was declined and why, on-screen, to retry or pick another tender.
          const reason = res.error || 'Card was not charged on the reader — try again.';
          setReaderResult({ kind: 'error', message: reason });
          toast.error(reason, { duration: 6000 });
          setProcessing(false);
          return;
        }
        terminalPaidPI = res.paymentIntentId || null;
        // HOLD the "Processing… — you can remove your card" screen a guaranteed minimum so the client sees
        // acceptance + pulls the card, instead of Tap → Approved flashing by (Jose 2026-10-22). readerBusy
        // stays TRUE through the hold so the overlay keeps showing the Processing phase. If the card-read
        // "Processing…" already displayed for a while during authorize, we only top up the remainder.
        const MIN_PROCESSING_MS = 2000;
        setReaderStatusMsg('Processing… — you can remove your card'); // force the Processing phase (matches isProcessing)
        const readAt = processingSinceRef.current || Date.now();
        const remain = MIN_PROCESSING_MS - (Date.now() - readAt);
        if (remain > 0) await new Promise((r) => setTimeout(r, remain));
        setReaderBusy(false);
        setReaderStatusMsg('');
        setReaderResult({ kind: 'success', message: `Card approved on the reader — $${amountDue.toFixed(2)} ✓` });
        toast.success('Card approved on the reader ✓');
        chargeApprovedAtRef.current = Date.now();
        setChargeApproved(true); // card cleared → show the big "Approved · finishing up" overlay now
      }

      // ── CARD-CHARGE GATE (Jose 2026-10-24: "Diana's Android recorded a sale but charged nothing") ──
      // A CARD payment MUST have actually been charged — on the reader (terminalPaidPI) OR a saved card
      // (cardOnFilePI) — before we record the sale. If neither happened (e.g. a phone/Android with NO
      // reader connected, or no terminal on this device), DO NOT record a phantom "paid" card sale.
      // Stop and tell the operator — no payment device → no fake success. Cash / gift / other tenders and
      // gift-card-covered tickets are unaffected (they never needed a card charge).
      // 🧪 STAGING TEST APPROVAL (Jose 2026-10-30): staging has NO physical reader, so a card leg — including
      // the CARD portion of a cash+card split — could never complete there, blocking end-to-end verification.
      // On STAGING ONLY, simulate an approval so the sale records and the split/reporting can be checked.
      // Hard-gated on REACT_APP_ENVIRONMENT==='staging' → this path does not exist in the prod build.
      let simulatedPI: string | null = null;
      if (process.env.REACT_APP_ENVIRONMENT === 'staging' &&
          paymentMethod === 'card' && amountDue > 0 && !terminalPaidPI && !cardOnFilePI && !fullyCoveredByGiftCard) {
        simulatedPI = `test_sim_${Date.now()}`;
        setReaderBusy(false);
        setReaderResult({ kind: 'success', message: `(Staging test) Card “approved” — $${amountDue.toFixed(2)} ✓` });
        toast.success('(Staging) simulated card approval ✓');
      }

      if (paymentMethod === 'card' && amountDue > 0 && !terminalPaidPI && !cardOnFilePI && !fullyCoveredByGiftCard && !simulatedPI) {
        setReaderBusy(false);
        setProcessing(false);
        const why = isTerminalAvailable()
          ? 'The card was NOT charged — no reader is connected to this device. Connect a reader and try again, or take another tender.'
          : 'This device has no card reader, so a card can’t be charged here. Use the register with the reader, or take cash / another tender.';
        setReaderResult({ kind: 'error', message: why });
        toast.error(why, { duration: 8000 });
        return;
      }

      // A real Stripe charge happened if EITHER the reader or the card-on-file path succeeded. On staging a
      // simulated approval stands in so the record completes (no real PI written — nothing to refund).
      const paidStripePI = terminalPaidPI || cardOnFilePI;
      const terminalPaidFields: any = (paidStripePI || simulatedPI)
        ? { paidOnline: true, ...(paidStripePI ? { stripePaymentIntentId: paidStripePI } : {}), paymentStatus: 'paid' }
        : {};

      // OFFLINE DEFERRED CHARGE (Jose 2026-08-28): if the device is offline and this is a CARD sale
      // for a known client, we can't reach Stripe now — so we record the sale and let the server
      // charge the client's card-on-file when this write syncs on reconnect (processDeferredCharge
      // Cloud Function). ONLY engages when truly offline + card + known client — online checkouts,
      // cash, gift-card-covered, and walk-ins with no client are completely unchanged.
      const deferCardCharge =
        (typeof navigator !== 'undefined' && navigator.onLine === false) &&
        paymentMethod === 'card' &&
        !!appointment.clientId &&
        !fullyCoveredByGiftCard;
      const deferredChargeFields: any = deferCardCharge ? {
        paymentStatus: 'awaiting_online',
        deferredCharge: {
          status: 'pending',
          amountCents: Math.round(total * 100),
          consent: true, // barber selected Card for a known client while offline = intent to charge on reconnect
          clientId: appointment.clientId,
          clientEmail: effClientEmail || null,
          clientName: appointment.clientName || null,
          createdAt: new Date().toISOString(),
          attempts: 0,
        },
      } : {};

      // Persist the AUTHORITATIVE charged figures so the record + receipt + refund reconcile to the card
      // (the tax-free reconstruction used to diverge). BEHAVIOR-PIN: plausible-but-wrong audit (2026-09-10).
      const checkoutTotalsFields = {
        ticketTotal: Math.round(total * 100) / 100,      // full ticket incl. tax, net of coins (pre-gift-card)
        taxAmount: Math.round(tax * 100) / 100,
        taxRatePct: taxRatePct || 0,
        // amountCharged = what actually moved through THIS terminal today. Prepaid = money was collected
        // earlier, so $0 hits the till now (the full ticketTotal still books as revenue → barber commission).
        amountCharged: isPrepaid ? 0 : Math.round(amountDue * 100) / 100, // what actually hit the card after gift card + split cash
        ...(slikCoinDiscount > 0 ? { slikCoinDiscount: Math.round(slikCoinDiscount * 100) / 100 } : {}),
        ...(giftCardRedeemApplied > 0 ? {
          giftCardRedeemApplied: Math.round(giftCardRedeemApplied * 100) / 100,
          // Stamp the card + its pre-redemption balance so the receipt can show "CODE $before → $after" (Jose 2026-11-01)
          giftCardCode: gcRedeemCard?.code || null,
          giftCardBalanceBefore: Math.round(((gcRedeemCard?.currentBalance) || 0) * 100) / 100,
        } : {}),
        // SPLIT TENDER (Jose 2026-10-30): cash collected alongside the card. Reporting peels this off the
        // card method → it counts as cash; the card method keeps only the remainder (amountCharged).
        ...(splitCashApplied > 0 ? { splitCashAmount: splitCashApplied } : {}),
      };

      if (appointment.id) {
        await appointmentService.updateAppointment(appointment.id, {
          status: 'completed',
          completedAt: new Date(),
          tipAmount: (isSplitTicket ? takeTip(primaryBarberId) : tipAmount),
          paymentMethod: aptPaymentMethod as 'cash' | 'card' | 'mobile' | 'store_credit' | 'gift_card',
          discountAmount: manualDiscount > 0 ? manualDiscount : undefined,
          discountReason: manualDiscount > 0 ? discountReason : undefined,
          discountNotes: manualDiscount > 0 && discountNotes ? discountNotes : undefined,
          ...(isPrepaid ? { prepaidNote: prepaidNote.trim(), prepaidAt: new Date() } : {}),
          productsSold: primaryProducts.length > 0 ? primaryProducts : undefined,
          ...(isSplitTicket ? {} : checkoutTotalsFields),
          // Walk-in tile edits on a booked ticket: reassign client / adjust price only if changed.
          ...(clientOverride ? { clientId: effClientId, clientName: effClientName } : {}),
          ...(priceOverride != null ? { price: priceOverride, priceOverridden: true, priceOverrideByRole: role, originalPrice: appointment.price ?? null } : {}),
          ...deferredChargeFields,
          ...terminalPaidFields,
        }, { background: true });   // card already cleared + saved locally → show the receipt/rebook NOW, sync in bg (Jose 2026-10-22)
        console.log(`[Checkout] Updated appointment ${appointment.id} with checkout data`);
      } else {
        // WALK-IN SALE from the Register — create the completed record. status:'completed' makes
        // appointmentService skip the booking-time guards (retroactive sale can't double-book).
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
        // 🧾 PRODUCT-ONLY SALE (Jose 2026-09-03): no service on the ticket, only product(s). Tag it so it's
        // a RETAIL sale (kept for product revenue + barber credit) but NOT shown as a calendar appointment.
        // A service ticket that also sells a product stays a normal appointment.
        const isProductOnlySale = !appointment.serviceName && !appointment.service && serviceItems.length === 0 && primaryProducts.length > 0;
        const created = await appointmentService.addAppointment({
          clientId: effClientId,
          clientName: effClientName || 'Walk-in Customer',
          clientPhone: effClientPhone,
          clientEmail: effClientEmail,
          barberId: appointment.barberId || 'unknown',
          barberName: appointment.barberName || 'Unknown',
          service: appointment.serviceName || appointment.service || 'Sale',
          serviceName: appointment.serviceName || appointment.service || 'Sale',
          price: displayServicePrice,
          duration: appointment.duration || 0,
          date: dateStr,
          time: timeStr,
          status: 'completed',
          source: 'walk-in',
          ...(isProductOnlySale ? { saleType: 'product' as const, service: 'Product Sale', serviceName: 'Product Sale' } : {}),
          completedAt: now,
          tipAmount: (isSplitTicket ? takeTip(primaryBarberId) : tipAmount),
          paymentMethod: aptPaymentMethod,
          discountAmount: manualDiscount > 0 ? manualDiscount : undefined,
          discountReason: manualDiscount > 0 ? discountReason : undefined,
          discountNotes: manualDiscount > 0 && discountNotes ? discountNotes : undefined,
          additionalItems: additionalItems.length > 0 ? additionalItems : undefined,
          productsSold: primaryProducts.length > 0 ? primaryProducts : undefined,
          ...(isSplitTicket ? {} : checkoutTotalsFields),
          ...(priceOverride != null ? { priceOverridden: true, priceOverrideByRole: role, originalPrice: appointment.price ?? null } : {}),
          ...deferredChargeFields,
          ...terminalPaidFields,
        } as any, { background: true });   // card already cleared → return the receipt NOW, sync the walk-in sale record in bg (Jose 2026-10-23)
        saleApptId = created?.id;
        console.log(`[Checkout] Created completed walk-in sale ${created?.id}`);
        // CHARGED-BUT-NOT-SAVED GUARD (Jose 2026-08-30): a local-only fallback id ('apt-…') means the
        // Firestore write FAILED — the sale will be wiped by the next sync. Surface the EXACT error
        // on-screen (toasts now render above every modal) so it's diagnosable without a console.
        if (created?.id && String(created.id).startsWith('apt-')) {
          const werr = (appointmentService as any).lastWriteError || 'unknown write error';
          setReaderResult({ kind: 'error', message: `Sale did NOT save to the cloud — ${werr}. Money moved but the record will be lost. Screenshot this.` });
          toast.error(`NOT SAVED: ${werr}`, { duration: 30000, id: 'save-fail' });
        }
      }

      // 🪙 DEBIT REDEEMED COINS (Jose 2026-10-31): the sale is recorded with the coin discount applied —
      // now subtract the redeemed coins from the client's real wallet (clients/{uid}.loyaltyPoints) in a
      // transaction, from the account that actually holds them (coinAccountId handles the wrong-record case).
      // Best-effort: a failed debit warns but never blocks the receipt (the discount was small; money moved).
      if (useSlikCoins && slikCoinDiscount > 0 && coinAccountId) {
        const coinsToRedeem = Math.round(slikCoinDiscount * 100);
        try {
          const nb = await redeemCoins(coinAccountId, coinsToRedeem, {
            appointmentId: saleApptId || appointment.id,
            clientName: appointment.clientName,
            dollarValue: slikCoinDiscount,
          });
          console.log(`[Checkout] Redeemed ${coinsToRedeem} coins → new balance ${nb}`);
        } catch (coinErr) {
          console.error('[Checkout] coin debit failed:', coinErr);
          toast.error('Coins could not be deducted — check the balance.', { duration: 6000 });
        }
      }

      // 🧑‍🤝‍🧑 SPLIT-TICKET CREDIT: any product sold by a DIFFERENT barber than the ticket owner gets
      // its own completed record, so reporting/payroll credit the barber who actually sold it. The
      // customer still paid one bill (handled above); this only fixes WHO gets the credit.
      const otherBarberIds = Array.from(new Set(otherBarberProducts.map(p => p.barberId)));
      for (const bId of otherBarberIds) {
        const theirProducts = otherBarberProducts.filter(p => p.barberId === bId);
        const bName = theirProducts[0]?.barberName || 'Unknown';
        try {
          const sNow = new Date();
          const sPad = (n: number) => String(n).padStart(2, '0');
          await appointmentService.addAppointment({
            clientId: appointment.clientId,
            clientName: appointment.clientName || 'Walk-in Customer',
            clientPhone: effClientPhone,
            clientEmail: effClientEmail,
            barberId: bId,
            barberName: bName,
            service: 'Product Sale',
            serviceName: 'Product Sale',
            saleType: 'product' as const, // 🧾 retail — off the calendar, kept for the seller's product credit
            price: 0,
            duration: 0,
            date: `${sNow.getFullYear()}-${sPad(sNow.getMonth() + 1)}-${sPad(sNow.getDate())}`,
            time: `${sPad(sNow.getHours())}:${sPad(sNow.getMinutes())}`,
            status: 'completed',
            source: 'walk-in',
            completedAt: sNow,
            tipAmount: 0,
            paymentMethod: aptPaymentMethod,
            productsSold: theirProducts,
          } as any, { background: true });   // secondary credit record — sync in bg, never hang the receipt (Jose 2026-10-23)
          console.log(`[Checkout] Split-ticket: credited ${bName} with ${theirProducts.length} product(s)`);
        } catch (splitErr) {
          console.error(`[Checkout] Failed to credit ${bName} for split products:`, splitErr);
        }
      }

      // ✂️ SERVICE SPLIT (walk-in Register AND booked appointment): every EXTRA service on the ticket
      // (a second barber's walk-in service, an ADDED service on a booked appointment, or a LINKED
      // appointment paid together) is credited to the barber who performed it. Two shapes, same idea —
      // whether a record already exists:
      //   • LINKED appointment (carries linkedAppointmentId) → an existing record → mark IT completed;
      //     its own barber keeps the credit. Never create a duplicate (that would double-count).
      //   • ADDED service (no record) → create a completed record crediting its barber (fallback: the
      //     ticket barber). Same as the walk-in path.
      // The primary service is already on the record above. We iterate EVERY service line: a LINKED
      // appointment always completes (even in a multi-service checkout — the split case), while an ADDED
      // service creates a record only when NOT multi-service (a multi-service appt's own components
      // belong to the primary appointment, so they're skipped).
      {
        for (const svc of serviceItems) {
          const svcBarberId = (svc as any).barberId || primaryBarberId;
          const svcBarberName = (svc as any).barberName || primaryBarberName;
          const linkedId = (svc as any).linkedAppointmentId as string | undefined;
          try {
            if (linkedId) {
              // Existing appointment paid together — complete its own record; credit stays with it.
              await appointmentService.updateAppointment(linkedId, {
                status: 'completed',
                completedAt: new Date(),
                paymentMethod: aptPaymentMethod as 'cash' | 'card' | 'mobile' | 'store_credit' | 'gift_card',
                ...(isSplitTicket ? { tipAmount: takeTip(svcBarberId) } : {}),
              }, { background: true });   // sync in bg — don't hang the receipt/rebook on a split-ticket write (Jose 2026-10-22)
              console.log(`[Checkout] Split-ticket: completed linked appointment ${linkedId} ("${svc.name}")`);
            } else if (!isMultiService) {
              // No record yet — create a completed one crediting the performing barber. (A multi-service
              // appt's own components are skipped here — they belong to the primary appointment above.)
              const vNow = new Date();
              const vPad = (n: number) => String(n).padStart(2, '0');
              await appointmentService.addAppointment({
                clientId: appointment.clientId,
                clientName: appointment.clientName || 'Walk-in Customer',
                clientPhone: effClientPhone,
                clientEmail: effClientEmail,
                barberId: svcBarberId,
                barberName: svcBarberName,
                service: svc.name,
                serviceName: svc.name,
                price: svc.price,
                duration: 0,
                date: `${vNow.getFullYear()}-${vPad(vNow.getMonth() + 1)}-${vPad(vNow.getDate())}`,
                time: `${vPad(vNow.getHours())}:${vPad(vNow.getMinutes())}`,
                status: 'completed',
                source: 'walk-in',
                completedAt: vNow,
                tipAmount: (isSplitTicket ? takeTip(svcBarberId) : 0),
                paymentMethod: aptPaymentMethod,
              } as any, { background: true });   // secondary credit record — sync in bg, never hang the receipt (Jose 2026-10-23)
              console.log(`[Checkout] Split-ticket: credited ${svcBarberName} for service "${svc.name}"`);
            }
          } catch (svcErr) {
            console.error(`[Checkout] Failed to credit service "${svc.name}":`, svcErr);
          }
        }
      }

      // 📦 CLOSE THE RETAIL LOOP (both paths): deduct each sold product from inventory + log a
      // 'sale' transaction attributed to THIS barber. Inventory hiccups must never fail a checkout —
      // the money already moved.
      for (const sold of productsSold) {
        const match = inventoryProducts.find(p => p.id === sold.productId);
        if (!match) {
          console.warn(`[Checkout] "${sold.productName}" not in inventory — sold but stock not deducted.`);
          continue;
        }
        try {
          await inventoryService.decrementForSale(
            match.id,
            sold.quantity,
            transaction.id,
            sold.barberId || primaryBarberId,
            sold.barberName || primaryBarberName
          );
        } catch (invErr) {
          console.error(`[Checkout] Failed to deduct "${sold.productName}" from inventory:`, invErr);
        }
      }

      // 🎁 GIFT CARDS: for every gift-card line in the cart, issue a real gift card now (a sale can
      // include one OR be just a gift card — both work). Deferred value → a live code in `giftCards`.
      const giftCardLines = additionalItems.filter((i: any) => i.type === 'giftcard');
      for (const gc of giftCardLines) {
        try {
          const card = await giftCardService.createGiftCard({
            templateId: 'default',
            initialValue: gc.price,
            recipientName: (gc as any).recipientName || appointment.clientName,
            recipientEmail: (gc as any).recipientEmail,
            personalMessage: (gc as any).personalMessage,
            purchasedByName: appointment.clientName,
            deliveryMethod: (gc as any).deliveryMethod || 'in-person',
            createdBy: 'pos-staff',
            code: (gc as any).code,               // physical card number, if staff entered one
            soldVia: 'pos',                       // brain attribution (2026-08-15)
            soldByBarberId: appointment.barberId, // credit the sale to this barber
          });
          toast.success(`🎁 Gift card issued — code ${card.code} ($${gc.price.toFixed(2)})`, { duration: 8000 });
        } catch (gcErr) {
          console.error('[Checkout] Failed to issue gift card:', gcErr);
          toast.error('⚠️ Payment took but a GIFT CARD failed to issue — tell the owner.', { duration: 9000 });
        }
      }

      // 🎁 REDEEM (2026-08-15): if a gift card was APPLIED to this ticket, draw it down NOW and log a
      // traceable redemption (barber + service/product/tip split via the brain). These dollars are NOT
      // new revenue — they were booked when the card was sold; reporting reads the redemption to keep
      // the books honest and to shrink the outstanding-liability number.
      if (gcRedeemCard && giftCardRedeemApplied > 0) {
        try {
          const breakdown = splitRedemption(giftCardRedeemApplied, {
            service: displayServicePrice,
            product: productTaxable,
            tip: tipAmount,
          });
          const res = await giftCardService.redeemGiftCard({
            code: gcRedeemCard.code,
            amount: giftCardRedeemApplied,
            relatedAppointmentId: saleApptId,
            processedBy: 'pos-staff',
            barberId: appointment.barberId,
            breakdown,
          });
          if (res.success) {
            toast.success(`🎁 Applied $${giftCardRedeemApplied.toFixed(2)} from gift card — ${res.message}`, { duration: 7000 });
            // Offer to save this card to the client's account (unless it's already linked here).
            if (appointment?.clientId && (gcRedeemCard as any).linkedClientId !== appointment.clientId) {
              setPendingGcLink({ giftCardId: gcRedeemCard.id, clientId: appointment.clientId, clientName: appointment.clientName || 'this client' });
            }
          } else {
            toast.error(`⚠️ Gift card not applied: ${res.message}`, { duration: 9000 });
          }
        } catch (gcErr) {
          console.error('[Checkout] Gift card redemption failed:', gcErr);
          toast.error('⚠️ Payment took but the GIFT CARD did not draw down — tell the owner.', { duration: 9000 });
        }
      }
    } catch (error) {
      // The money already moved, so we don't hard-fail the checkout — but this MUST be visible, not
      // swallowed: a silently-dropped sale = missing barber credit + missing revenue. (Jose 2026-08-13)
      console.error('Error recording checkout sale:', error);
      const emsg = (error as any)?.message || String(error);
      setReaderResult({ kind: 'error', message: `Payment took but the sale did NOT save — ${emsg}. Screenshot this and tell the owner.` });
      toast.error(`⚠️ NOT SAVED: ${emsg}`, { duration: 30000, id: 'save-fail' });
    }

    setProcessing(false);
    setChargeApproved(false); // hand off to the big checkout celebration below
    toast.success('💳 Payment processed successfully!');

    // REVIEW REQUEST (Jose 2026-08-26): queue a review ask per the cadence rules. Fully
    // non-blocking + swallowed — a review hiccup can NEVER affect a completed checkout.
    // Only fires if enabled in Settings → Reviews; the brain decides ask-or-suppress.
    if (appointment?.clientId) {
      enqueueForCheckout({
        clientId: appointment.clientId,
        clientName: appointment.clientName,
        clientPhone: effClientPhone,
        clientEmail: effClientEmail,
        barberId: appointment.barberId,
        barberName: appointment.barberName,
        appointmentId: saleApptId || appointment.id,
      }).catch(() => {});
    }

    // CHECKOUT CELEBRATION (Jose 2026-08-07): the "big pop-up after we check somebody out".
    // Modal-mode success card + randomized confetti (CelebrationProvider). Fires here so it
    // survives the checkout modal closing (rendered at app root, z-10000).
    // Build the receipt for the card's action row (Email/Text/Print/Void) via the shared receipts brain.
    // Override .total with the ACTUAL charged total (incl. tax / coin-discount) so the card matches the till.
    const cardReceipt = buildReceipt({
      id: saleApptId || appointment.id || '',
      clientName: appointment.clientName,
      clientId: appointment.clientId,
      barberId: appointment.barberId,
      barberName: appointment.barberName,
      service: serviceName,
      date: (appointment as any).date,
      time: (appointment as any).time,
      price: displayServicePrice,
      tipAmount,
      discountAmount: manualDiscount,
      paymentMethod: (isPrepaid ? 'prepaid' : fullyComped ? 'comp' : paymentMethod === 'slik-coins' ? 'store_credit' : paymentMethod) ?? undefined, // 'comp'/'prepaid' for $0-today tickets (not blank)
      productsSold: productLines.map((p: any) => ({ productName: p.name, quantity: 1, unitPrice: p.price, total: p.price, barberId: p.barberId, barberName: p.barberName })),
      completedAt: new Date(),
    });
    cardReceipt.total = total;

    // GUARANTEE the "Approved ✓" overlay is actually SEEN (Jose 2026-10-23): with background writes the
    // work between Approved and here is now instant, so the overlay flashed past. Hold ~1.4s from when
    // Approved first showed before swapping to the "Checkout complete" modal. (Only waits when a charge
    // cleared — cash/gift-card skip chargeApproved, so chargeApprovedAtRef stays 0 and there's no delay.)
    if (chargeApprovedAtRef.current) {
      const APPROVED_MIN_MS = 1400;
      const held = Date.now() - chargeApprovedAtRef.current;
      if (held < APPROVED_MIN_MS) await new Promise((r) => setTimeout(r, APPROVED_MIN_MS - held));
      chargeApprovedAtRef.current = 0;
    }

    celebrate({
      mode: 'modal',
      autoDismissMs: 30000,   // clear the "Checkout complete" card off the station after 30s (Jose 2026-10-22)
      title: 'Checkout complete! 🎉',
      subtitle: `${appointment.clientName || 'Client'} · $${total.toFixed(2)}`,
      rows: [
        { label: 'Barber', value: appointment.barberName || 'Staff' },
        { label: 'Service', value: serviceName },
        ...(tipAmount > 0 ? [{ label: 'Tip', value: `$${tipAmount.toFixed(2)}` }] : []),
        { label: 'Total', value: `$${total.toFixed(2)}` },
      ],
      // Receipt action row (Jose 2026-08-19): Email/Text/Print the receipt + Refund/Void, right here so
      // "email me that receipt" doesn't mean hunting the Receipts tab.
      renderContent: (close) => (
        <CheckoutReceiptActions
          receipt={cardReceipt}
          appointmentId={saleApptId || appointment.id || ''}
          clientEmail={effClientEmail}
          clientPhone={effClientPhone}
          onClose={close}
        />
      ),
      // Rebook is now the primary button (Jose 2026-08-19); the card's ✕ closes without rebooking.
      actions: onRebook
        ? [{ label: 'Rebook →', primary: true, onClick: onRebook }]
        : [{ label: 'Done', primary: true }],
    });

    // ── TIER 1: FLAGGED DISCOUNT = CHECKOUT GOES THROUGH + INSTANT NOTIFICATIONS ──
    // Manager gets SMS alert, client gets receipt showing original price + discount
    if (fraudAnalysis && manualDiscount > 0 && fraudAnalysis.severity !== 'none' && fraudAnalysis.severity !== 'low') {
      // Create approval request in Firebase (for audit trail + manager dashboard)
      if (appointment?.barberId) {
        try {
          await discountApprovalService.createApprovalRequest(
            appointment.barberId,
            appointment.barberName || 'Unknown',
            {
              appointmentId: appointment.id || '',
              clientId: appointment.clientId || '',
              barberId: appointment.barberId,
              barberName: appointment.barberName || 'Unknown',
              clientName: appointment.clientName || 'Unknown',
              originalPrice: subtotal + manualDiscount,
              discountedPrice: subtotal,
              discountAmount: manualDiscount,
              discountPercent: Math.round((manualDiscount / (subtotal + manualDiscount)) * 100),
              discountReason,
              reasonNotes: discountNotes,
              paymentMethod: (paymentMethod === 'slik-coins' ? 'digital_coins' : paymentMethod === 'mobile' ? 'other' : paymentMethod) as 'cash' | 'card' | 'digital_coins' | 'other',
              serviceStartTime: apptWindow.start || new Date(Date.now() - 60 * 60 * 1000),
              serviceEndTime: apptWindow.end || new Date(),
              requestTime: new Date(),
              barberStrikeCount: barberStrikes
            },
            fraudAnalysis
          );
        } catch (err) {
          console.error('Failed to create approval request:', err);
        }

        // Send manager SMS alert (non-blocking)
        sendManagerAlerts(fraudAnalysis, fraudAnalysis.severity);
      }
    }

    // Send client receipt SMS (non-blocking, for ALL completed checkouts with a phone number) — routed
    // through SLIK Connect's durable queue (source 'checkout_receipt') so the receipt THREADS + is labeled
    // in the inbox and survives a WiFi blip. The processOutboundSms CF suppresses it where SMS is off
    // (staging). (Jose 2026-10-30)
    if (effClientPhone) {
      communicationService.sendToClient({
        clientId: appointment.clientId,
        clientName: appointment.clientName,
        phone: effClientPhone,
        appointmentId: appointment.id,
        source: 'checkout_receipt',
        includeSaveContact: false,
        body: buildCheckoutReceiptBody({
          clientName: appointment.clientName,
          barberName: appointment.barberName || 'Staff',
          serviceName: serviceName,
          originalPrice: displayServicePrice,
          discountAmount: manualDiscount,
          tipAmount: tipAmount,
          totalCharged: total,
        }),
      }).catch(err => console.error('Failed to queue client receipt:', err));
    }

    // Fire AI nudge in background (non-blocking)
    if (appointment.barberId) {
      aiManagerAPI.generateNudge(appointment.barberId, 'revenue', `Just completed checkout for ${appointment.clientName}, $${total.toFixed(2)}`)
        .then(nudge => { if (nudge) setAiNudge(nudge); })
        .catch(() => {});
    }

    // Call the payment complete callback (parent will handle closing if needed)
    console.log('💰 SmartCheckoutModal: Payment processing complete');
    console.log('💰 onPaymentComplete callback exists?', !!onPaymentComplete);
    if (onPaymentComplete) {
      console.log('💰 Calling onPaymentComplete with transaction:', transaction);
      onPaymentComplete(transaction);
      console.log('💰 onPaymentComplete called successfully');
    } else {
      console.warn('💰 No onPaymentComplete callback - using backward compatibility mode');
      // Only close if there's no callback handler (backwards compatibility)
      onClose();
    }
  };

  const handleSendPaymentLink = async () => {
    // Validate phone number
    if (!customerPhone || !isValidPhoneNumber(customerPhone)) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setSendingPaymentLink(true);

    try {
      const customerName = appointment.clientName;
      // Sanitize phone number for email (remove all non-digits)
      const sanitizedPhone = (effClientPhone || '')?.replace(/\D/g, '');
      const customerEmail = effClientEmail || (sanitizedPhone ? `${sanitizedPhone}@slik-pos.com` : undefined);

      // Build items array
      const items = [];
      if (appointment.additionalItems && appointment.additionalItems.length > 0) {
        appointment.additionalItems.forEach(item => {
          items.push({
            name: item.name,
            price: item.price,
            quantity: 1,
            description: item.type === 'service' ? `Service by ${appointment.barberName || 'Staff'}` : 'Product',
          });
        });
      } else {
        items.push({
          name: serviceName,
          price: displayServicePrice,
          quantity: 1,
          description: `Service by ${appointment.barberName || 'Staff'}`,
        });
      }

      const result = await sendMobilePaymentLink(
        {
          customerPhone,
          customerName,
          customerEmail,
          items,
          total: total,
          tip: tipAmount,
          tax: tax,
          metadata: {
            appointmentId: appointment.id || '',
            posTransaction: 'true',
            customerId: appointment.clientId || '',
            barberName: appointment.barberName || '',
          },
        },
        'SLIK POS'
      );

      if (result.success) {
        // 💳 PAYMENT-LINK AUTO-COMPLETE (Jose 2026-08-21): stash a PENDING snapshot on the appointment
        // (no side effects — safe if the customer never pays). The stripeWebhook reads this and completes
        // the sale the moment Stripe confirms payment, so a paid link lands in reporting + barber credit
        // (service + tip + this barber's products) with no manual step. V1 = single-barber ticket (the
        // norm for a texted link); multi-barber SERVICE split + gift-card issuance via link are deferred
        // (steer those to card/cash). BEHAVIOR-PIN: project-slik-pos-payments-sms-integration-2026-08-21
        if (appointment.id) {
          try {
            const linkProducts = additionalItems
              .filter((i: any) => i.type === 'product')
              .map((i: any) => ({
                productId: (i as any).productId || `product-${i.name.toLowerCase().replace(/\s+/g, '-')}`,
                productName: i.name,
                quantity: 1,
                unitPrice: i.price,
                total: i.price,
                barberId: (i as any).barberId || appointment.barberId || 'unknown',
                barberName: (i as any).barberName || appointment.barberName || 'Unknown',
              }));
            const pendingPayment: any = { total, tipAmount, paymentMethod: 'mobile', taxAmount: Math.round(tax * 100) / 100, amountCharged: Math.round(amountDue * 100) / 100 };
            if (linkProducts.length) pendingPayment.productsSold = linkProducts;
            if (manualDiscount > 0) {
              pendingPayment.discountAmount = manualDiscount;
              pendingPayment.discountReason = discountReason;
              if (discountNotes) pendingPayment.discountNotes = discountNotes;
            }
            await appointmentService.updateAppointment(appointment.id, {
              paymentStatus: 'awaiting_online',
              paymentLinkSentAt: new Date(),
              pendingPayment,
            } as any);
          } catch (stashErr) {
            // Non-fatal: the link still works; the webhook falls back to completing the base service.
            console.error('[PaymentLink] Failed to stash pending sale snapshot:', stashErr);
          }
        }
        setPaymentLinkGenerated(result.paymentLink || null);

        // Generate QR code for the payment link
        if (result.paymentLink) {
          const qrCode = await createPaymentQRCode(result.paymentLink);
          setPaymentQRCode(qrCode);
        }

        // Show the mobile payment modal with QR code
        setShowMobilePaymentModal(true);

        if (result.smsSent) {
          // HONEST STATUS (Jose 2026-10-31): the link is QUEUED through SLIK Connect, not yet confirmed
          // delivered — the real sent/delivered/failed status live-updates in the client's thread. Don't
          // claim "sent ✓" (that was the email "fake success" trap). Point staff to the thread.
          toast.success(`📱 Payment link sent to ${customerPhone} — track delivery in SLIK Connect`);
        } else {
          toast('⚠️ Payment link created but the text could not be queued. Share the link manually below.', {
            duration: 6000,
          });
        }
      } else {
        toast.error(result.error || 'Failed to create payment link');
      }
    } catch (error: any) {
      console.error('Error sending payment link:', error);
      toast.error(error.message || 'Failed to send payment link');
    } finally {
      setSendingPaymentLink(false);
    }
  };

  // Copy payment link to clipboard
  const handleCopyPaymentLink = () => {
    if (!paymentLinkGenerated) return;

    navigator.clipboard.writeText(paymentLinkGenerated);
    toast.success('📋 Payment link copied to clipboard!');
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/60 transition-opacity duration-300"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      style={{ backdropFilter: 'blur(4px)' }}
    >
      <div
        className="absolute top-0 right-0 h-[100dvh] w-full max-w-2xl slik-modal-surface slik-checkout-v4 shadow-2xl flex flex-col pb-safe co-panel co-anim"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="slik-modal-header px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-white">Checkout</h2>
              <p className="text-white/90 text-sm">{appointment.clientName}</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/90 hover:text-white hover:bg-white/10 transition-colors slik-interactive"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 flex flex-col min-h-0 scroll-fix co-grid">
          {/* co-details column (≥768px): order summary + client. Phone: flows as single column. */}
          <div className="co-details">
          {/* Dynamic Pricing Banner */}
          {dynamicPricing && dynamicPricing.suggestedPrice !== displayServicePrice && (
            <div className="mb-2 px-2.5 py-1.5 slik-chip rounded-md" style={{ borderColor: 'rgba(130,140,220,0.3)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="h-3 w-3 text-indigo-400 flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-white">AI Suggested Price</span>
                </div>
                <span className="text-sm font-bold text-indigo-400">${dynamicPricing.suggestedPrice.toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-white/90 mt-0.5">{dynamicPricing.reasoning}</p>
            </div>
          )}

          {/* Fraud Alert Banner */}
          {fraudAlert && (
            <div className="mb-2 px-2.5 py-2 rounded-md" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Shield className="h-3 w-3 text-red-400 flex-shrink-0" />
                <span className="text-[11px] font-bold text-red-300">Fraud Alert - Flagged</span>
              </div>
              <ul className="mb-1.5">
                {fraudAlert.reasons.map((reason: string, idx: number) => (
                  <li key={idx} className="text-[10px] text-red-300/70">• {reason}</li>
                ))}
              </ul>
              <div className="flex gap-1.5">
                <button onClick={() => { setFraudAlert(null); handlePayment(); }} className="px-2 py-1 bg-red-600 text-white rounded text-[10px] font-medium">Override</button>
                <button onClick={() => setFraudAlert(null)} className="slik-btn-ghost px-2 py-1 rounded text-[10px] font-medium">Cancel</button>
              </div>
            </div>
          )}

          {/* Service Summary */}
          <div className="slik-chip co-card rounded-lg px-3 py-2.5 mb-2.5">
            <div
              className="flex items-center justify-between cursor-pointer touch-manipulation"
              onClick={() => { setPriceInput(displayServicePrice.toFixed(2)); setEditingService(v => !v); }}
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-white truncate">{formatServiceName(serviceName)}</h3>
                <p className="text-xs text-white/90">{appointment.duration || 30} min{(appointment.barberName || appointment.barberId) ? ` • ${appointment.barberName || appointment.barberId}` : ''} · <span className="text-purple-300 font-medium">tap to edit</span></p>
              </div>
              <p className="text-xl font-bold text-white ml-2">${displayServicePrice.toFixed(2)}</p>
            </div>
            {manualDiscount > 0 && (
              <div className="flex items-center justify-between text-[11px] mt-1 text-emerald-300">
                <span>Discount{discountReason ? ` · ${discountReasons.find(r => r.id === discountReason)?.label || discountReason}` : ''}</span>
                <span>−${manualDiscount.toFixed(2)}</span>
              </div>
            )}
            {editingService && (
              <div className="mt-2 pt-2 slik-divider border-t space-y-2" onClick={(e) => e.stopPropagation()}>
                {/* Duration is set on the appointment card / service tile BEFORE checkout — not here (Jose 2026-09-03). */}
                {/* PRICE change is OWNER/ADMIN only (Jose 2026-08-30) — everyone else uses Discount.
                    The change is stamped with the operator's role for the PIN-tracked audit trail. */}
                {isOwner ? (
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] text-white/70 w-24 flex-shrink-0">Service price</label>
                    <div className="flex items-center flex-1 bg-black/20 border border-white/10 rounded-lg px-2">
                      <span className="text-white/60 text-sm">$</span>
                      <input type="number" inputMode="decimal" step="0.01" min="0" value={priceInput}
                        onChange={(e) => setPriceInput(e.target.value)}
                        className="bg-transparent flex-1 px-1 py-1.5 text-sm text-white outline-none"
                        placeholder={displayServicePrice.toFixed(2)} />
                    </div>
                  </div>
                ) : (
                  <p className="text-[11px] text-white/50 flex items-center gap-1"><Shield className="w-3 h-3" /> Price changes are owner/admin only — use Discount below.</p>
                )}
                {/* DISCOUNT (Jose 2026-09-03): tap a preset configured in Settings — its type/value ($/%/comp)
                    auto-computes the amount off the service price. No free-form entry; barbers can trigger. */}
                <div>
                  <label className="text-[11px] text-white/70 mb-1 block">Discount</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={() => { setSelectedDiscountId(null); setDiscountAmount(''); setDiscountNotes(''); }}
                      className={`px-2 py-1.5 rounded-lg text-xs font-semibold border touch-manipulation ${!selectedDiscountId ? 'bg-white/15 border-white/30 text-white' : 'bg-black/20 border-white/10 text-white/70'}`}
                    >No discount</button>
                    {discountReasons.map(r => {
                      const discounted = (r.type && r.value != null) ? computeDiscountedPrice(displayServicePrice, r.type, r.value) : displayServicePrice;
                      const delta = Math.max(0, Math.round((displayServicePrice - discounted) * 100) / 100);
                      const effect = r.type === 'comp' ? 'Complimentary' : r.type === 'percent' ? `${r.value}% off` : r.type === 'fixed' ? `$${r.value} off` : '';
                      const sel = selectedDiscountId === r.id;
                      return (
                        <button key={r.id}
                          onClick={() => { setSelectedDiscountId(r.id); setDiscountAmount(String(delta)); setDiscountReason(mapReasonToFraud(r.id)); setDiscountNotes(r.label); }}
                          className={`px-2 py-1.5 rounded-lg text-xs font-semibold border text-left touch-manipulation ${sel ? 'bg-emerald-500/25 border-emerald-400/60 text-emerald-100' : 'bg-black/20 border-white/10 text-white/80'}`}
                        >
                          <span className="block truncate">{r.label}</span>
                          {effect && <span className="block text-[10px] opacity-80">{effect}{delta > 0 ? ` · −$${delta.toFixed(2)}` : ''}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-2 pt-0.5">
                  <button onClick={() => {
                      if (isOwner) {
                        const p = parseFloat(priceInput);
                        setPriceOverride(!isNaN(p) && p >= 0 ? Math.round(p * 100) / 100 : null);
                      }
                      setEditingService(false);
                      slikSaved('Saved!');
                    }}
                    className="slik-btn-primary slik-interactive flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold touch-manipulation">Apply</button>
                  <button onClick={() => { setPriceOverride(null); setPriceInput(''); setDiscountAmount(''); setDiscountNotes(''); setSelectedDiscountId(null); setEditingService(false); }}
                    className="slik-btn-ghost slik-interactive px-3 py-1.5 rounded-lg text-xs font-medium touch-manipulation">Reset</button>
                </div>
              </div>
            )}
            {serviceItems.length > 0 && (
              <div className="mt-1.5 pt-1.5 slik-divider border-t">
                {serviceItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-[11px] py-0.5 group">
                    <span className="text-white/90">
                      {item.name}
                      {/* PER-BARBER: show who performed each extra service on a split ticket */}
                      {(item as any).barberName ? <span className="text-white/60"> • {(item as any).barberName}</span> : ''}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-white/90">${item.price.toFixed(2)}</span>
                      {/* Trashcan to remove an added extra service (Jose 2026-10-23) */}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeExtraService(item); }}
                        aria-label={`Remove ${item.name}`}
                        className="p-1 rounded text-white/40 hover:text-red-400 hover:bg-white/10 touch-manipulation"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {productLines.length > 0 && (
              <div className="mt-1.5 pt-1.5 slik-divider border-t">
                {/* PRODUCTS — clearly represented in checkout (Jose 2026-08-09): distinct label + icon
                    so add-on products read separately from services; high-contrast text. */}
                <div className="flex items-center gap-1 mb-1">
                  <ShoppingBag className="w-3 h-3 text-purple-300" />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-purple-300">Products</span>
                </div>
                {productLines.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-0.5">
                    <span className="text-white/90">{item.name}</span>
                    <span className="font-semibold text-white">${item.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            {giftCardLinesDisplay.length > 0 && (
              <div className="mt-1.5 pt-1.5 slik-divider border-t">
                {/* GIFT CARDS — their OWN category, NEVER under Products, never taxed (Jose 2026-08-15).
                    Products are taxed + reported as product revenue; a gift card is neither. */}
                <div className="flex items-center gap-1 mb-1">
                  <Gift className="w-3 h-3 text-emerald-300" />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-300">Gift Cards</span>
                  <span className="text-[9px] text-white/50">· not taxed</span>
                </div>
                {giftCardLinesDisplay.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-0.5">
                    <span className="text-white/90">{item.name}</span>
                    <span className="font-semibold text-white">${item.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Client Information Card */}
          <div className="mb-2.5 px-3 py-2.5 slik-chip co-card rounded-lg">
            <div className="flex items-center justify-between mb-1.5 cursor-pointer touch-manipulation" onClick={() => { setClientQuery(''); setEditingClient(v => !v); }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: 'rgba(99,102,241,0.3)', border: '1px solid rgba(99,102,241,0.4)' }}>
                  {(effClientName || 'W').charAt(0).toUpperCase()}
                </div>
                <div>
                  <span className="text-sm font-bold text-white">{effClientName || 'Walk-in Customer'}</span>
                  {appointment.clientBadges?.cpiTier && (
                    <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      appointment.clientBadges.cpiTier === 'VIP' ? 'bg-purple-500 text-white' :
                      appointment.clientBadges.cpiTier === 'Loyal' ? 'bg-blue-500 text-white' :
                      'bg-gray-500 text-white'
                    }`}>{appointment.clientBadges.cpiTier}</span>
                  )}
                  {appointment.clientBadges?.loyaltyTier && (
                    <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      appointment.clientBadges.loyaltyTier === 'Platinum' ? 'bg-gray-600 text-white' :
                      appointment.clientBadges.loyaltyTier === 'Gold' ? 'bg-yellow-600 text-white' :
                      appointment.clientBadges.loyaltyTier === 'Silver' ? 'bg-gray-500 text-white' :
                      'bg-orange-500 text-white'
                    }`}>{appointment.clientBadges.loyaltyTier}</span>
                  )}
                </div>
              </div>
              <span className="text-[11px] text-purple-300 font-medium flex-shrink-0">tap to change</span>
            </div>
            {editingClient && (
              <div className="mb-2 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                <input autoFocus value={clientQuery} onChange={(e) => setClientQuery(e.target.value)}
                  placeholder="Search saved clients, or type a new name"
                  className="w-full bg-black/20 border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white outline-none placeholder-white/40" />
                {clientQuery.trim().length >= 1 && (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-white/10">
                    {clientDataService.searchClients(clientQuery).slice(0, 6).map((c) => (
                      <button key={c.id}
                        onClick={() => { setClientOverride({ id: c.id, name: `${c.firstName} ${c.lastName}`.trim(), phone: c.phone || undefined, email: c.email || undefined }); setEditingClient(false); }}
                        className="w-full flex items-center justify-between px-2.5 py-2 text-left hover:bg-white/5 border-b border-white/5 touch-manipulation">
                        <span className="text-sm text-white font-medium truncate">{c.firstName} {c.lastName}</span>
                        <span className="text-[11px] text-white/50 ml-2 flex-shrink-0">{c.phone}</span>
                      </button>
                    ))}
                    <button onClick={() => { setClientOverride({ id: undefined, name: clientQuery.trim() }); setEditingClient(false); }}
                      className="w-full flex items-center px-2.5 py-2 text-left hover:bg-white/5 text-purple-300 touch-manipulation">
                      <span className="text-sm font-medium truncate">+ Use “{clientQuery.trim()}” as new customer</span>
                    </button>
                  </div>
                )}
                {clientOverride && (
                  <button onClick={() => { setClientOverride(null); setEditingClient(false); }}
                    className="text-[11px] text-white/60 hover:text-white/90 touch-manipulation">Clear · back to {appointment.clientName || 'Walk-in Customer'}</button>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              {effClientPhone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                  <span className="font-medium text-white/90">{effClientPhone}</span>
                </div>
              )}
              {effClientEmail && (
                <div className="flex items-center gap-1 truncate">
                  <span className="text-white/80">Email:</span>
                  <span className="font-medium text-white/90 truncate">{effClientEmail}</span>
                </div>
              )}
              {appointment.clientBadges?.visitCount && (
                <div className="flex items-center gap-1">
                  <span className="text-white/80">Visits:</span>
                  <span className="font-medium text-white/90">{appointment.clientBadges.visitCount}</span>
                </div>
              )}
              {appointment.clientBadges?.totalSpent && (
                <div className="flex items-center gap-1">
                  <span className="text-white/80">Spent:</span>
                  <span className="font-medium text-white/90">${(appointment.clientBadges?.totalSpent || 0).toFixed(0)}</span>
                </div>
              )}
              {appointment.clientBadges?.memberSince && (
                <div className="flex items-center gap-1">
                  <span className="text-white/80">Since:</span>
                  <span className="font-medium text-white/90">{new Date(appointment.clientBadges.memberSince).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                </div>
              )}
              {appointment.clientBadges?.cpiScore !== undefined && (
                <div className="flex items-center gap-1">
                  <span className="text-white/80">CPI:</span>
                  <span className="font-medium text-white/90">{appointment.clientBadges.cpiScore}</span>
                </div>
              )}
            </div>
          </div>
          </div>{/* /co-details */}

          {/* co-choices column (≥768px): payment + tip + coins. */}
          <div className="co-choices">
          {/* Payment Method — tiles ENLARGED for the client-facing checkout (Jose 2026-09-30, task #44):
              the CLIENT taps these now, so Card/Cash/Link/Gift Card are big, easy targets. The price
              breakdown's mt-auto (below) absorbs the extra height, so it still fits on-screen without
              scrolling (iPad — the real client surface — has even more room than the phone preview). */}
          <div className="mb-2.5">
            <h3 className="font-semibold text-sm mb-2 text-white">Payment Method</h3>
            <div className="grid grid-cols-4 gap-2.5 co-methods">
              {/* CASH FIRST, then CREDIT (Jose 2026-10-19): clients check out on the iPad — cash is the
                  common/simple tender, so it leads; "Credit" (the card reader) is second. */}
              <button
                onClick={() => { setPaymentMethod('cash'); if (amountDue > 0) { setCashTenderDraft(''); setShowCashTenderModal(true); } }}
                className={`slik-interactive flex flex-col items-center justify-center gap-2 py-7 rounded-xl transition-colors text-base font-semibold touch-manipulation ${
                  paymentMethod === 'cash' ? 'slik-chip-selected text-white' : 'slik-chip text-white/90'
                }`}
              >
                <DollarSign className="w-6 h-6" />
                <span>Cash</span>
              </button>
              <button
                onClick={() => { setPaymentMethod('card'); setShowCreditTipModal(true); }}
                className={`slik-interactive flex flex-col items-center justify-center gap-2 py-7 rounded-xl transition-colors text-base font-semibold touch-manipulation ${
                  paymentMethod === 'card' ? 'slik-chip-selected text-white' : 'slik-chip text-white/90'
                }`}
              >
                <CreditCard className="w-6 h-6" />
                <span>Credit</span>
                {isTerminalAvailable() && (
                  <span className="text-[10px] leading-none text-white/70">M2 reader</span>
                )}
              </button>
              <button
                onClick={() => { setCustomerPhone(effClientPhone || ''); setShowMobilePaymentModal(true); }}
                className="slik-interactive slik-chip flex flex-col items-center justify-center gap-1.5 py-5 rounded-xl transition-all text-sm font-semibold text-white/90 touch-manipulation"
              >
                <Send className="w-6 h-6" />
                <span>Link</span>
              </button>
              <button
                onClick={() => setShowGiftCardPay(v => !v)}
                className={`slik-interactive flex flex-col items-center justify-center gap-2 py-7 rounded-xl transition-colors text-base font-semibold touch-manipulation ${
                  (showGiftCardPay || gcRedeemCard) ? 'slik-chip-selected text-white' : 'slik-chip text-white/90'
                }`}
              >
                <Gift className="w-6 h-6 text-emerald-300" />
                <span>Gift Card</span>
              </button>
            </div>

            {/* ALREADY PAID / PREPAID (Jose 2026-10-26): a STAFF action for when the client already paid for
                THIS visit earlier (e.g. a parent prepaid last week for the kid's cut). Keeps the full price
                (barber commission + revenue still count) but charges nothing now. Distinct from the 4 big
                client tiles — it's the barber's call, not a client tap. */}
            <button
              onClick={() => setPaymentMethod(isPrepaid ? null : 'prepaid')}
              className={`slik-interactive mt-2 w-full flex items-center justify-center gap-2 py-3 rounded-xl transition-colors text-sm font-semibold touch-manipulation ${
                isPrepaid ? 'slik-chip-selected text-white' : 'slik-chip text-white/80'
              }`}
            >
              <Check className="w-4 h-4 text-emerald-300" />
              <span>{isPrepaid ? 'Already paid ✓' : 'Already paid (prepaid)'}</span>
            </button>
            {/* Required audit note for a prepaid checkout — who collected the money + when. */}
            {isPrepaid && (
              <div className="mt-2">
                <input
                  autoFocus
                  value={prepaidNote}
                  onChange={(e) => setPrepaidNote(e.target.value)}
                  placeholder="Who paid & when? e.g. “Dad paid cash Oct 19”"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                />
                <p className="mt-1 text-[11px] text-white/50">Full price still counts for the barber — nothing is charged now.</p>
              </div>
            )}

            {/* SPLIT: PART CASH + REST CARD (Jose 2026-10-30). The operator enters the cash they collected;
                it draws the ticket down (like a gift card) and the card reader charges whatever's left.
                Hidden when the ticket is already $0 / prepaid / comp — there's nothing to split. */}
            {!isPrepaid && !fullyComped && !fullyCoveredByGiftCard && total > 0 && (
              <>
                <button
                  onClick={() => { const next = !showSplit; setShowSplit(next); if (!next) setSplitCashInput(''); }}
                  className={`slik-interactive mt-2 w-full flex items-center justify-center gap-2 py-3 rounded-xl transition-colors text-sm font-semibold touch-manipulation ${
                    splitCashApplied > 0 ? 'slik-chip-selected text-white' : 'slik-chip text-white/80'
                  }`}
                >
                  <DollarSign className="w-4 h-4 text-emerald-300" />
                  <span>{splitCashApplied > 0 ? `Split · $${splitCashApplied.toFixed(2)} cash collected` : 'Split (part cash + card)'}</span>
                </button>
                {showSplit && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-white/60 text-lg font-semibold">$</span>
                      <input
                        inputMode="decimal"
                        value={splitCashInput}
                        onChange={(e) => setSplitCashInput(e.target.value.replace(/[^0-9.]/g, ''))}
                        placeholder="Cash collected"
                        className="flex-1 px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 text-base focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                      />
                    </div>
                    {splitCashApplied > 0 && (
                      <p className="mt-1.5 text-[12px] text-emerald-200">
                        ${splitCashApplied.toFixed(2)} cash
                        {amountDue > 0
                          ? <> · <span className="text-white/80">${amountDue.toFixed(2)} left — tap <b>Credit</b> (or Link) to charge the rest</span></>
                          : <> · <span className="text-white/80">covers the whole ticket — records as a cash sale</span></>}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* CARD ON FILE (Jose 2026-09-06): only when Card is the tender AND this client has a saved
              card. Two sub-choices — charge the saved card off-session (no tap), or fall back to the
              M2 reader / manual entry. Defaults to the saved card. Hidden entirely for walk-ins. */}
          {paymentMethod === 'card' && cardOnFile && (
            <div className="mb-2.5">
              <div className="grid grid-cols-2 gap-2 co-methods">
                <button
                  onClick={() => setChargeToCardOnFile(true)}
                  className={`slik-interactive flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg transition-all text-xs font-medium touch-manipulation ${
                    chargeToCardOnFile ? 'slik-chip-selected text-white' : 'slik-chip text-white/90'
                  }`}
                >
                  <span className="flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Card on file</span>
                  <span className="text-[10px] text-white/70">
                    {cardOnFile.pm.brand.charAt(0).toUpperCase() + cardOnFile.pm.brand.slice(1)} •••• {cardOnFile.pm.last4}
                  </span>
                </button>
                <button
                  onClick={() => setChargeToCardOnFile(false)}
                  className={`slik-interactive flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg transition-all text-xs font-medium touch-manipulation ${
                    !chargeToCardOnFile ? 'slik-chip-selected text-white' : 'slik-chip text-white/90'
                  }`}
                >
                  <span className="flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> {isTerminalAvailable() ? 'Tap / insert' : 'Enter card'}</span>
                  <span className="text-[10px] text-white/70">{isTerminalAvailable() ? 'M2 reader' : 'manual'}</span>
                </button>
              </div>
            </div>
          )}

          {/* PAY WITH GIFT CARD (2026-08-16): revealed by the Gift Card payment button. The card draws
              the ticket down; any remainder pays on the method above. Covers "use it all up" AND partial. */}
          {(showGiftCardPay || gcRedeemCard) && (
          <div className="mb-2.5">
            <h3 className="font-semibold text-sm mb-2 text-white flex items-center gap-1.5">
              <Gift className="w-4 h-4 text-emerald-300" /> Pay with Gift Card
            </h3>
            {!gcRedeemCard ? (
              <>
                {/* Cards saved to this client — tap to use, no typing (Jose 2026-11-01). */}
                {clientGiftCards.length > 0 && (
                  <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-wide text-white/50 mb-1">On this account — tap to use</div>
                    <div className="flex flex-wrap gap-2">
                      {clientGiftCards.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => applyLinkedGiftCard(c)}
                          className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-lg bg-black/60 border border-white/15 hover:border-[#c0824a] text-white text-sm touch-manipulation"
                          title={`Use gift card ${c.code}`}
                        >
                          <Gift className="w-4 h-4 text-[#e0b341]" />
                          <span className="font-semibold">${(c.currentBalance || 0).toFixed(2)}</span>
                          <span className="text-white/50 text-xs font-mono">•••• {c.code.slice(-4)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                <input
                  value={gcRedeemCode}
                  onChange={(e) => { setGcRedeemCode(e.target.value.toUpperCase()); setGcRedeemError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') validateRedeemCard(); }}
                  placeholder="Enter gift card code"
                  className="slik-input flex-1 px-3 py-2 rounded-lg text-sm"
                />
                <button
                  onClick={validateRedeemCard}
                  disabled={gcValidating || !gcRedeemCode.trim()}
                  className="slik-chip slik-interactive px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40"
                >
                  {gcValidating ? '…' : 'Apply'}
                </button>
                </div>
              </>
            ) : (
              <div className="slik-chip px-3 py-2 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="text-xs">
                    <div className="text-emerald-300 font-semibold">🎁 {gcRedeemCard.code}</div>
                    <div className="text-white/70">
                      Balance ${gcRedeemCard.currentBalance.toFixed(2)} · applying ${giftCardRedeemApplied.toFixed(2)}
                      {giftCardRedeemApplied < total && <span className="text-white/50"> · ${amountDue.toFixed(2)} still due</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => { setGcRedeemCard(null); setGcRedeemCode(''); setGcRedeemError(''); }}
                    className="text-white/70 hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {/* INLINE "add this card to this client" — right on the line, no post-checkout popup (Jose 2026-11-01) */}
                {appointment?.clientId && (
                  (gcRedeemCard as any).linkedClientId === appointment.clientId ? (
                    <div className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-emerald-300">
                      <Check className="w-3.5 h-3.5" /> On {appointment.clientName || 'this client'}'s account
                    </div>
                  ) : (
                    <button
                      onClick={linkAppliedCardToClient}
                      disabled={gcLinkingInline}
                      className="mt-1.5 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[12px] font-bold text-white disabled:opacity-60 touch-manipulation"
                      style={{ background: 'linear-gradient(90deg,#1c1a17,#c0824a)' }}
                    >
                      <Gift className="w-3.5 h-3.5" />
                      {gcLinkingInline ? 'Saving…' : `Add this gift card to ${appointment.clientName || 'this client'}`}
                    </button>
                  )
                )}
              </div>
            )}
            {gcRedeemError && <p className="text-[11px] text-red-400 mt-1">{gcRedeemError}</p>}
            {gcRedeemCard && amountDue > 0 && (
              <p className="text-[11px] text-amber-300 mt-1">
                ${amountDue.toFixed(2)} left after the gift card — pay the rest with the method above ({paymentMethod === 'cash' ? 'Cash' : paymentMethod === 'mobile' ? 'Link' : 'Card'}).
              </p>
            )}
          </div>
          )}

          {/* Tip row MOVED into the Credit modal (Jose 2026-10-19) — tapping Credit pops the big tip
              picker. Removing the inline row gives the payment-method buttons room to be bigger. */}

          {/* SLIK Coins */}
          <div className="mb-2.5 flex items-center justify-between px-3 py-2.5 slik-chip co-card rounded-lg" style={{ borderColor: 'rgba(234,179,8,0.2)' }}>
            <div className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium text-white">SLIK Coins</span>
              <span className="text-xs text-white/90">
                {slikCoinsAvailable > 0
                  ? `(${slikCoinsAvailable} available · $${(slikCoinsAvailable / 100).toFixed(2)})`
                  : '(none on this account)'}
              </span>
            </div>
            <label className={`relative inline-flex items-center touch-manipulation ${slikCoinsAvailable > 0 ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}>
              <input type="checkbox" checked={useSlikCoins} disabled={slikCoinsAvailable <= 0} onChange={(e) => setUseSlikCoins(e.target.checked)} className="sr-only peer" />
              <div className="w-10 h-5 bg-white/10 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-transparent after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white/40 after:border after:border-transparent after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600 peer-checked:after:bg-white"></div>
            </label>
          </div>

          {/* Applied per-service discounts — set in the appointment card (Jose 2026-08-09). The discount
              lives in the appointment now; this is READ-ONLY here, but the fraud gate still runs. */}
          {serviceDiscounts.length > 0 && (
            <div className="mb-2 px-2.5 py-2 slik-chip rounded-md space-y-1.5" style={{ borderColor: 'rgba(249,115,22,0.2)' }}>
              <div className="flex items-center gap-1.5">
                <Shield className="w-3 h-3 text-orange-400" />
                <span className="text-xs font-medium text-white/90">Discounts applied</span>
                {barberStrikes > 0 && (
                  <span className="text-[10px] px-1.5 py-0 rounded-full font-medium" style={{ background: 'rgba(239,68,68,0.2)', color: '#f87171' }}>{barberStrikes} strikes</span>
                )}
              </div>
              {serviceDiscounts.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <span className="text-white/90 truncate mr-2">{formatServiceName(d.serviceName)} · {d.reasonLabel}</span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-white/40 line-through">${d.originalPrice}</span>
                    <span className="font-semibold text-white">${d.discountedPrice}</span>
                  </span>
                </div>
              ))}
              {discountAmount && parseFloat(discountAmount) > 0 && (
                <div className={`px-2 py-1.5 rounded-md border text-[11px] ${
                  fraudAnalysis?.severity === 'critical' ? 'border-red-500/30 text-red-300' :
                  fraudAnalysis?.severity === 'high' ? 'border-orange-500/30 text-orange-300' :
                  fraudAnalysis?.severity === 'medium' ? 'border-yellow-500/30 text-yellow-300' :
                  'border-green-500/30 text-green-300'
                }`} style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-white/80">Fraud Analysis</span>
                    <span className={`px-1.5 py-0 rounded-full text-[10px] font-bold text-white ${
                      fraudAnalysis?.severity === 'critical' ? 'bg-red-600' :
                      fraudAnalysis?.severity === 'high' ? 'bg-orange-600' :
                      fraudAnalysis?.severity === 'medium' ? 'bg-yellow-600' : 'bg-green-600'
                    }`}>{fraudAnalysis?.severity.toUpperCase() || 'LOW'} ({fraudAnalysis?.riskScore || 0})</span>
                  </div>
                  {fraudAnalysis && fraudAnalysis.fraudFlags.length > 0 && (
                    <div className="mt-0.5 text-[10px] text-white/90">
                      {fraudAnalysis.fraudFlags.map((flag, idx) => (
                        <span key={idx} className="mr-1">• {flag.replace(/_/g, ' ')}</span>
                      ))}
                    </div>
                  )}
                  {fraudAnalysis?.severity === 'critical' && (
                    <div className="mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}>
                      🚫 Checkout BLOCKED — manager approval required
                    </div>
                  )}
                  {fraudAnalysis?.severity === 'high' && (
                    <div className="mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(251,146,60,0.15)', color: '#fb923c', border: '1px solid rgba(251,146,60,0.3)' }}>
                      Manager will be notified after checkout
                    </div>
                  )}
                  {fraudAnalysis?.severity === 'medium' && (
                    <div className="mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(250,204,21,0.15)', color: '#facc15', border: '1px solid rgba(250,204,21,0.3)' }}>
                      Manager will be notified after checkout
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </div>{/* /co-choices */}

          {/* co-summary column (≥768px): running total + banners + pay/cancel actions. */}
          <div className="co-summary">
          {/* 👨‍👩‍👧 Family-group reminder (Jose 2026-10-25) — info only; names the others booked together. */}
          {familyGroupMembers.length > 0 && (
            <div className="rounded-lg px-3 py-2.5 mb-2.5 bg-pink-500/20 border border-pink-300/40">
              <div className="text-[12px] font-extrabold text-pink-100 flex items-center gap-1">
                👨‍👩‍👧 Family group — {familyGroupMembers.length + 1} booked together
              </div>
              <div className="text-[11px] text-white/85 mt-1 leading-snug">
                Also in this group: {familyGroupMembers.map(m => `${m.name} (${m.service})`).join(', ')}.
                <span className="text-white/60"> Check each out separately for now.</span>
              </div>
            </div>
          )}
          {/* Price Breakdown - Pushed to bottom */}
          <div className="slik-chip co-card rounded-lg px-3 py-2.5 mb-2.5 mt-auto">
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-white/90">{extraServicesBillable > 0 ? 'Services' : 'Service'}</span>
                {/* PER-BARBER: include the billed extra services so the breakdown adds up to the subtotal */}
                <span className="font-medium text-white/90">${(displayServicePrice + extraServicesBillable).toFixed(2)}</span>
              </div>
              {additionalItemsTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/90">Add-ons</span>
                  <span className="font-medium text-white/90">${additionalItemsTotal.toFixed(2)}</span>
                </div>
              )}
              {manualDiscount > 0 && (
                <div className="flex justify-between text-orange-400">
                  <span>Discount</span>
                  <span className="font-medium">-${manualDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-white/90">Tip</span>
                <span className="font-medium text-white/90">${tipAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/90">Tax (9%)</span>
                <span className="font-medium text-white/90">${tax.toFixed(2)}</span>
              </div>
              {useSlikCoins && slikCoinDiscount > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Coins Discount</span>
                  <span className="font-medium">-${slikCoinDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="slik-divider border-t pt-1.5 mt-1.5 flex justify-between items-center">
                <span className="font-bold text-sm text-white">Total</span>
                <span className="font-bold text-xl text-white">${total.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-yellow-500">
                <span className="flex items-center gap-1"><Coins className="w-3.5 h-3.5" /> Earn</span>
                <span className="font-bold">+{slikCoinsEarned} coins</span>
              </div>
            </div>
          </div>

          {/* AI Coaching Nudge */}
          {aiNudge && (
            <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-3 flex items-start gap-2">
              <TrendingUp className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-blue-300">AI Coach</p>
                <p className="text-xs text-blue-100/95 mt-0.5">{aiNudge.message}</p>
              </div>
              <button onClick={() => setAiNudge(null)} className="text-blue-400/60 hover:text-blue-300 flex-shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Blocked Checkout Banner */}
          {checkoutBlocked && (
            <div className="px-3 py-2 rounded-lg border border-red-500/40 text-xs" style={{ background: 'rgba(239,68,68,0.1)' }}>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-400 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-300">Checkout blocked by fraud detection</p>
                  <p className="text-red-200/70 mt-0.5">Manager has been notified. Remove the discount or wait for manager approval to proceed.</p>
                </div>
              </div>
              <button
                onClick={() => { setCheckoutBlocked(false); setDiscountAmount(''); setServiceDiscounts([]); }}
                className="mt-1.5 w-full px-2 py-1 rounded bg-red-600/20 border border-red-500/30 text-red-300 text-[11px] font-medium hover:bg-red-600/30 transition-colors touch-manipulation"
              >
                Remove Discount & Continue
              </button>
            </div>
          )}

          {/* Card-reader result banner (Jose 2026-08-30): persistent, high-contrast outcome of the
              last M2 charge. A decline stays on screen with the exact reason until the barber retries
              or dismisses — no more silent resets or Stripe-log scavenger hunts. */}
          {readerResult && (
            <div
              className={`px-3 py-2 rounded-lg border text-xs ${readerResult.kind === 'error' ? 'border-red-500/40' : 'border-emerald-500/40'}`}
              style={{ background: readerResult.kind === 'error' ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)' }}
            >
              <div className="flex items-start gap-2">
                {readerResult.kind === 'error'
                  ? <CreditCard className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  : <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <p className={`font-semibold ${readerResult.kind === 'error' ? 'text-red-300' : 'text-emerald-300'}`}>
                    {readerResult.kind === 'error' ? 'Card was not charged' : 'Card approved'}
                  </p>
                  <p className={`mt-0.5 ${readerResult.kind === 'error' ? 'text-red-200/80' : 'text-emerald-200/80'}`}>
                    {readerResult.message}
                  </p>
                </div>
                <button
                  onClick={() => setReaderResult(null)}
                  className={`text-[11px] px-2 py-0.5 rounded ${readerResult.kind === 'error' ? 'text-red-300 hover:bg-red-600/20' : 'text-emerald-300 hover:bg-emerald-600/20'} touch-manipulation`}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                // Cancel must ABORT an in-flight reader scan/collect immediately (Jose 2026-08-30) —
                // not let the native BLE discovery keep running until it times out.
                if (readerBusy) {
                  cancelReaderOperation();
                  toast.dismiss('m2reader');
                  setReaderBusy(false);
                  setProcessing(false);
                }
                onClose();
              }}
              className="slik-btn-ghost slik-interactive flex-1 px-3 py-2.5 rounded-lg font-medium text-sm touch-manipulation"
            >
              {readerBusy ? 'Cancel (stop reader)' : 'Cancel'}
            </button>
            <button
              onClick={handlePayment}
              disabled={processing || checkoutBlocked}
              className={`slik-btn-primary co-cta slik-interactive flex-1 px-3 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center disabled:opacity-50 touch-manipulation ${checkoutBlocked ? 'cursor-not-allowed' : ''}`}
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {readerBusy ? 'Waiting for card…' : cofBusy ? 'Charging card on file…' : 'Processing...'}
                </>
              ) : checkoutBlocked ? (
                <>
                  <Shield className="w-4 h-4 mr-1.5" />
                  Blocked
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-1.5" />
                  Complete Payment
                </>
              )}
            </button>
          </div>
          </div>{/* /co-summary */}
        </div>
      </div>

      {/* BIG CENTERED CHARGE OVERLAY (Jose 2026-10-19): while the card is being charged, the client is
          staring at the iPad — show the LIVE status front-and-center (not a corner toast they miss), so
          they know to present their card and wait. Driven by readerBusy/cofBusy + readerStatusMsg. */}
      {(readerBusy || cofBusy || chargeApproved) && (() => {
        // ONE continuous big overlay for the WHOLE charge (Jose 2026-10-21) — never dead air:
        //   connecting/processing → "Tap your card" → "Payment Approved · finishing up" → (celebration).
        // z above any sub-modal (tip picker / cash pad) so the status is never hidden behind another modal.
        // FOUR distinct client-facing phases (Jose 2026-10-21, #132) so the screen always matches reality:
        //   CONNECTING (make PI / find / connect / update) → TAP (reader ready) → PROCESSING (card read,
        //   the beep — authorizing) → APPROVED. The old single "Processing payment" header showed during
        //   connecting (before any tap) AND stayed on "tap" after the beep — both confusing.
        const msg = readerStatusMsg || '';
        const isTap = !chargeApproved && /tap|insert|swipe|present/i.test(msg);
        // card-on-file (cofBusy) is always "processing" — there's no reader step; else match ONLY explicit
        // processing words. (2026-10-22) MUST NOT match the LOOKING phase — the old regex caught "charge"
        // in "Starting the charge…" and showed "Processing…" before any tap, so clients tapped too early.
        const isProcessing = !chargeApproved && !isTap && (cofBusy || /processing|authoriz|confirm|approv/i.test(msg));
        return (
          <div className="fixed inset-0 z-[10070] flex items-center justify-center bg-black/85 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-full max-w-md rounded-3xl bg-[#20242e] border-2 border-white/15 shadow-2xl p-10 text-center">
              {chargeApproved ? (
                <>
                  <div className="relative mx-auto mb-4 w-28 h-28 flex items-center justify-center">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400/25 animate-ping" />
                    <div className="relative w-28 h-28 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
                      <Check className="w-16 h-16 text-white" strokeWidth={3} />
                    </div>
                  </div>
                  <h2 className="text-3xl font-extrabold text-white mb-1">Approved ✓</h2>
                  <p className="text-base text-white/70 mb-6 flex items-center justify-center gap-2">
                    <span className="w-4 h-4 rounded-full border-2 border-white/25 border-t-emerald-300 animate-spin" />
                    All set — finishing up…
                  </p>
                </>
              ) : isTap ? (
                <>
                  <div className="relative mx-auto mb-3 w-28 h-28 flex items-center justify-center">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400/25 animate-ping" />
                    <div className="relative w-28 h-28 rounded-full bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center">
                      <CreditCard className="w-14 h-14 text-emerald-300" />
                    </div>
                  </div>
                  <ChevronDown className="w-10 h-10 mx-auto text-emerald-300 animate-bounce mb-2" />
                  <h2 className="text-3xl font-extrabold text-white mb-2">Tap your card on the reader</h2>
                  <p className="text-lg text-white/70 mb-6">Hold it close if tap doesn’t catch right away</p>
                </>
              ) : isProcessing ? (
                <>
                  <div className="mx-auto mb-6 w-20 h-20 rounded-full border-4 border-white/20 border-t-emerald-400 animate-spin" />
                  <h2 className="text-3xl font-extrabold text-white mb-2">Processing…</h2>
                  <p className="text-lg text-emerald-300/90 mb-6">{readerBusy ? 'Card read — you can remove it now' : 'Charging…'}</p>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-6 w-16 h-16 rounded-full border-4 border-white/20 border-t-purple-400 animate-spin" />
                  <h2 className="text-3xl font-extrabold text-white mb-2">Looking for the reader…</h2>
                  <p className="text-lg text-white/70 mb-6 min-h-[1.75rem]">Please wait — don’t tap yet.</p>
                </>
              )}
              <div className="text-4xl font-extrabold text-emerald-400">${amountDue.toFixed(2)}</div>

              {/* CANCEL THE CHARGE (Jose 2026-10-23): before the card is read there was NO way out of
                  "Looking for the reader" / "Tap your card" except completing a payment or hard-resetting
                  the app (which drops the reader link). This kills the in-flight collect/scan and returns
                  to checkout — the reader stays connected. Hidden once the card is being processed/approved
                  (too late to safely abort). */}
              {!chargeApproved && !isProcessing && (
                <button
                  onClick={() => {
                    cancelRequestedRef.current = true;     // tell the charge handler this is a cancel, not a decline
                    cancelReaderOperation();               // abort the native collect/scan (reader stays connected)
                    toast.dismiss('m2reader');
                    setReaderBusy(false); setCofBusy(false);
                    setReaderStatusMsg(''); setReaderResult(null);
                    setProcessing(false); setChargeApproved(false);
                    processingSinceRef.current = 0;
                  }}
                  className="mt-7 w-full py-3.5 rounded-2xl bg-white/10 border border-white/25 text-white font-bold text-lg active:scale-[0.99] transition-transform touch-manipulation"
                >
                  Cancel charge
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* TIP CELEBRATION burst (Jose 2026-10-19): escalates with the % — sparkles → fireworks. */}
      {tipBurst && (() => {
        const { tier, key } = tipBurst;
        const emoji = ['', '✨', '✨', '🎉', '🎊', '🎆'][tier] || '✨';
        const count = tier * 7;
        const size = 18 + tier * 8;
        return (
          <div key={key} className="fixed inset-0 z-[10060] pointer-events-none flex items-center justify-center">
            {Array.from({ length: count }).map((_, i) => {
              const angle = (Math.PI * 2 * i) / count + Math.random() * 0.6;
              const dist = 70 + Math.random() * (50 + tier * 45);
              const style = {
                position: 'absolute',
                fontSize: `${size}px`,
                ['--dx' as any]: `${Math.cos(angle) * dist}px`,
                ['--dy' as any]: `${Math.sin(angle) * dist}px`,
                animation: `slik-tip-burst ${0.7 + Math.random() * 0.4}s ease-out forwards`,
              } as React.CSSProperties;
              return <span key={i} style={style}>{emoji}</span>;
            })}
            {tier >= 5 && <span style={{ fontSize: '72px', animation: 'slik-tip-pop 0.8s ease-out forwards' } as React.CSSProperties}>🎆</span>}
          </div>
        );
      })()}

      {/* BIG CENTERED DECLINE OVERLAY (Jose 2026-10-19): a card decline must be in-your-face, not a
          corner toast — show it big with a clear "Try again", then fall back to another tender. */}
      {readerResult?.kind === 'error' && (
        <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/85 p-6">
          <div className="w-full max-w-md rounded-3xl bg-[#20242e] border-2 border-red-500/40 shadow-2xl p-10 text-center">
            <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
              <X className="w-9 h-9 text-red-400" />
            </div>
            <h2 className="text-3xl font-extrabold text-white mb-2">Payment not completed</h2>
            <p className="text-lg text-white/80 mb-6">{readerResult.message}</p>
            <button
              onClick={() => setReaderResult(null)}
              className="w-full bg-white/10 border border-white/20 text-white py-4 rounded-2xl font-bold text-lg active:scale-[0.99] transition-transform"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* CASH TENDER CALCULATOR (Jose 2026-10-19): big, in the client's face — "what did you hand me?"
          → shows the change. Display-only; the recorded sale is always the amount due. */}
      {showCashTenderModal && (() => {
        const tenderNum = parseFloat(cashTenderDraft) || 0;
        const change = tenderNum - amountDue;
        const enough = tenderNum >= amountDue;
        const quick = Array.from(new Set([Math.ceil(amountDue), 20, 40, 60, 100].filter((v) => v >= amountDue))).slice(0, 4);
        const appendCash = (d: string) => setCashTenderDraft((prev) => {
          if (d === '.' && prev.includes('.')) return prev;
          if (d !== '.' && prev.includes('.') && (prev.split('.')[1]?.length ?? 0) >= 2) return prev;
          return (prev + d).replace(/^0+(?=\d)/, '');
        });
        return (
          <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/85 p-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-full max-w-md rounded-3xl bg-[#20242e] border-2 border-white/15 shadow-2xl p-6 text-center">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-extrabold text-white flex items-center gap-2"><DollarSign className="w-6 h-6 text-emerald-400" /> Cash</h2>
                <button onClick={() => setShowCashTenderModal(false)} className="p-2 rounded-lg text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
              </div>
              <div className="text-white/70 text-lg">Amount due</div>
              <div className="mb-3 text-4xl font-extrabold text-white">${amountDue.toFixed(2)}</div>
              <div className="mb-4 rounded-2xl bg-black/30 border border-white/10 px-4 py-4">
                <div className="text-white/60 text-sm mb-1">Cash received</div>
                <div className="text-4xl font-extrabold text-emerald-400">${cashTenderDraft || '0'}</div>
                {enough && tenderNum > 0 && (
                  <div className="mt-2 text-xl font-bold text-white">Change <span className="text-emerald-300">${change.toFixed(2)}</span></div>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {quick.map((v) => (
                  <button key={v} onClick={() => setCashTenderDraft(String(v))} className="py-3 rounded-xl bg-white/10 border border-white/15 text-white font-bold text-lg active:bg-white/20 touch-manipulation">${v}</button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'].map((d) => (
                  <button key={d} onClick={() => appendCash(d)} className="py-4 rounded-xl bg-white/10 border border-white/15 text-white text-2xl font-bold active:bg-white/20 touch-manipulation">{d}</button>
                ))}
                <button onClick={() => setCashTenderDraft((prev) => prev.slice(0, -1))} className="py-4 rounded-xl bg-white/10 border border-white/15 text-white active:bg-white/20 flex items-center justify-center touch-manipulation"><Delete className="w-6 h-6" /></button>
              </div>
              <button
                onClick={() => { cashConfirmedRef.current = true; setShowCashTenderModal(false); handlePayment(); }}
                disabled={!enough}
                className="w-full py-4 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-xl disabled:opacity-40 active:scale-[0.99] transition-transform touch-manipulation"
              >
                {enough ? `Complete — $${amountDue.toFixed(2)}` : `Need $${(amountDue - tenderNum).toFixed(2)} more`}
              </button>
              <button
                onClick={() => setShowCashTenderModal(false)}
                className="w-full mt-2 py-3 rounded-2xl bg-white/5 border border-white/15 text-white/80 font-semibold text-base active:scale-[0.99] transition-transform touch-manipulation"
              >+ Add a product first</button>
            </div>
          </div>
        );
      })()}

      {/* TIP-ON-CREDIT popup (Jose 2026-10-19): big tip picker, pops when the client taps Credit. */}
      {showCreditTipModal && (
        <div className="fixed inset-0 z-[10048] flex items-center justify-center bg-black/85 p-4" onClick={(e) => { e.stopPropagation(); setShowCreditTipModal(false); }}>
          <div className="w-full max-w-md rounded-3xl bg-[#20242e] border-2 border-white/15 shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-2xl font-extrabold text-white">Add a tip</h2>
              <button onClick={() => setShowCreditTipModal(false)} className="p-2 rounded-lg text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            <p className="text-white/60 mb-4">On ${tipBaseForCalc.toFixed(2)}</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => { setTipPercentage(0); setCustomTip(''); setTipSplit(null); }}
                className={`py-5 rounded-2xl text-lg font-bold border-2 touch-manipulation ${tipPercentage === 0 && !customTip ? 'bg-white/15 border-white/40 text-white' : 'bg-white/5 border-white/15 text-white/80'}`}
              >No Tip</button>
              {tipPresets.map((preset) => {
                const amt = (tipBaseForCalc * preset) / 100;
                const sel = tipPercentage === preset && !customTip;
                return (
                  <button
                    key={preset}
                    onClick={() => { setTipPercentage(preset); setCustomTip(''); setTipSplit(null); fireTipBurst(preset); }}
                    className={`py-5 rounded-2xl text-lg font-bold border-2 touch-manipulation ${sel ? 'bg-emerald-500/25 border-emerald-400 text-white' : 'bg-white/5 border-white/15 text-white/80'}`}
                  >
                    <div>{preset}%</div>
                    <div className="text-sm text-emerald-300">${amt.toFixed(2)}</div>
                  </button>
                );
              })}
              <button
                onClick={() => {
                  setShowCreditTipModal(false);
                  // MULTI-BARBER TICKET → the per-barber split layout (barber photos + keypad each).
                  // Single-barber → the plain Custom $/% popup. (Jose 2026-11-03: Custom on a split ticket
                  // must show each barber, not one lumped tip.)
                  if (isSplitTicket) {
                    const seed: Record<string, { mode: '$' | '%'; val: string }> = {};
                    serviceBarbers.forEach(b => { seed[b.barberId] = { mode: '%', val: tipPercentage ? String(tipPercentage) : '' }; });
                    setTipSplitDraft(seed);
                    setShowTipSplitModal(true);
                  } else {
                    setShowCustomTipModal(true);
                  }
                }}
                className="py-5 rounded-2xl text-lg font-bold border-2 bg-white/5 border-white/15 text-white/80 touch-manipulation"
              >Custom</button>
            </div>
            <div className="mb-4 text-center text-xl font-extrabold text-white">Tip: <span className="text-emerald-300">${tipAmount.toFixed(2)}</span></div>
            {/* CHECK OUT = straight to the reader (minimal movement — Jose 2026-10-19). */}
            <button
              onClick={() => { setShowCreditTipModal(false); handlePayment(); }}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-extrabold text-xl active:scale-[0.99] transition-transform touch-manipulation"
            >Check Out →</button>
            <button
              onClick={() => setShowCreditTipModal(false)}
              className="w-full mt-2 py-3 rounded-2xl bg-white/5 border border-white/15 text-white/80 font-semibold text-base active:scale-[0.99] transition-transform touch-manipulation"
            >+ Add a product first</button>
          </div>
        </div>
      )}

      {/* CUSTOM TIP popup (Jose 2026-08-10): hit "Custom" → choose a $ amount OR % via a clean
          keypad + $/% toggle (like Fresha's "Add a tip"). Replaces the old number-spinner input.
          MUST live OUTSIDE the animated/scroll panel (sibling of it, like MobilePaymentModal) — a
          transformed+overflow ancestor traps position:fixed and clips this popup. See panel @ slideInRight. */}
      {showCustomTipModal && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/70" onClick={(e) => { e.stopPropagation(); setShowCustomTipModal(false); }}>
          <div className="w-full max-w-md rounded-3xl p-6 shadow-2xl" style={{ background: '#151b2b', border: '1px solid rgba(148,163,184,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-extrabold text-white text-xl">Custom Tip</h3>
              <button onClick={() => setShowCustomTipModal(false)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-center mb-3">
              <span className="text-4xl font-bold text-white">
                {customTipMode === '$' ? '$' : ''}{customTipDraft || '0'}{customTipMode === '%' ? '%' : ''}
              </span>
              <div className="text-xs text-white/60 mt-1">
                {customTipMode === '%'
                  ? `= $${((subtotal * (parseFloat(customTipDraft) || 0)) / 100).toFixed(2)} tip`
                  : (subtotal > 0 ? `${Math.round(((parseFloat(customTipDraft) || 0) / subtotal) * 100)}% of $${subtotal.toFixed(2)}` : 'tip amount')}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={() => setCustomTipMode('$')} className={`py-2 rounded-lg text-sm font-semibold transition-all ${customTipMode === '$' ? 'bg-purple-600 text-white' : 'bg-white/10 text-white/70'}`}>$ Amount</button>
              <button onClick={() => { setCustomTipMode('%'); setCustomTipDraft(d => d.replace('.', '')); }} className={`py-2 rounded-lg text-sm font-semibold transition-all ${customTipMode === '%' ? 'bg-purple-600 text-white' : 'bg-white/10 text-white/70'}`}>% Percent</button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {['1','2','3','4','5','6','7','8','9'].map(n => (
                <button key={n} onClick={() => appendTipDigit(n)} className="py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-semibold active:scale-95 transition-all">{n}</button>
              ))}
              <button onClick={() => appendTipDigit('.')} disabled={customTipMode === '%'} className="py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-semibold disabled:opacity-30 active:scale-95 transition-all">.</button>
              <button onClick={() => appendTipDigit('0')} className="py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-semibold active:scale-95 transition-all">0</button>
              <button onClick={() => setCustomTipDraft(d => d.slice(0, -1))} className="py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white active:scale-95 transition-all flex items-center justify-center"><Delete className="w-5 h-5" /></button>
            </div>
            <button onClick={applyCustomTip} className="w-full py-3 rounded-lg bg-gradient-to-r from-purple-600 to-purple-500 text-white font-bold active:scale-95 transition-all">Add Tip</button>
          </div>
        </div>
      )}

      {/* PER-BARBER TIP SPLIT MODAL (2026-08-17) — photos from the appointment cards, %/$ per barber */}
      {showTipSplitModal && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/70" onClick={(e) => { e.stopPropagation(); setShowTipSplitModal(false); }}>
          <div className="w-full max-w-md rounded-3xl p-6 shadow-2xl" style={{ background: '#20242e', border: '2px solid rgba(255,255,255,0.15)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-extrabold text-white text-2xl">Split the tip</h2>
              <button onClick={() => setShowTipSplitModal(false)} className="p-2 rounded-lg text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            <p className="text-sm text-white/60 mb-4">Tap a barber's amount, then use the keypad</p>
            <div className="space-y-2.5 mb-4">
              {serviceBarbers.map(b => {
                const d = tipSplitDraft[b.barberId] || { mode: '%' as const, val: '' };
                const dollars = d.mode === '%' ? round2(((parseFloat(d.val) || 0) / 100) * b.serviceTotal) : round2(parseFloat(d.val) || 0);
                const photo = barberPhoto(b.barberId);
                const active = activeTipBarberId === b.barberId;
                return (
                  <div key={b.barberId} className={`flex items-center gap-3 rounded-2xl px-2 py-2 transition-all ${active ? 'bg-purple-500/20 ring-2 ring-purple-400' : ''}`}>
                    {photo
                      ? <img src={photo} alt={b.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                      : <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center bg-purple-600/40 text-white text-base font-bold">{b.name.charAt(0).toUpperCase()}</div>}
                    <div className="flex-1 min-w-0">
                      <div className="text-base text-white font-semibold truncate">{b.name}</div>
                      <div className="text-xs text-white/50">service ${b.serviceTotal.toFixed(2)}</div>
                    </div>
                    {/* %/$ toggle */}
                    <button
                      onClick={() => setTipSplitDraft(prev => ({ ...prev, [b.barberId]: { mode: (prev[b.barberId]?.mode === '%' ? '$' : '%'), val: (prev[b.barberId]?.mode === '%' ? (prev[b.barberId]?.val || '') : (prev[b.barberId]?.val || '').replace('.', '')) } }))}
                      className="w-11 h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white text-base font-bold flex-shrink-0"
                    >{d.mode}</button>
                    {/* Tappable value → opens the in-app keypad for THIS barber (no native keyboard). */}
                    <button
                      onClick={() => setActiveTipBarberId(active ? null : b.barberId)}
                      className={`w-20 h-11 rounded-xl text-center text-base font-semibold flex-shrink-0 ${active ? 'bg-white/25 text-white ring-1 ring-white/40' : 'bg-white/10 text-white hover:bg-white/20'}`}
                    >{d.mode === '$' ? '$' : ''}{d.val || '0'}{d.mode === '%' ? '%' : ''}</button>
                    <div className="w-16 text-right text-base font-bold text-white flex-shrink-0">${dollars.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>

            {activeTipBarberId ? (() => {
              // ── In-app keypad for the highlighted barber (mirrors the Custom Tip calculator) ──
              const id = activeTipBarberId;
              const ab = serviceBarbers.find(x => x.barberId === id);
              const d = tipSplitDraft[id] || { mode: '%' as const, val: '' };
              const dollars = d.mode === '%' ? round2(((parseFloat(d.val) || 0) / 100) * (ab?.serviceTotal || 0)) : round2(parseFloat(d.val) || 0);
              const setVal = (fn: (v: string) => string) => setTipSplitDraft(prev => { const c = prev[id] || { mode: '%' as const, val: '' }; return { ...prev, [id]: { mode: c.mode, val: fn(c.val) } }; });
              const append = (dg: string) => setVal(v => {
                if (dg === '.') return (v.includes('.') || d.mode === '%') ? v : (v === '' ? '0.' : v + '.');
                const next = (v === '0' || v === '') ? dg : v + dg;
                return next.length <= 6 ? next : v;
              });
              return (
                <div className="rounded-xl bg-black/25 p-3 mb-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-white/70">Set tip for <b className="text-white">{ab?.name}</b></span>
                    <span className="text-sm font-bold text-white">{d.mode === '$' ? '$' : ''}{d.val || '0'}{d.mode === '%' ? '%' : ''} <span className="text-white/50 text-xs">= ${dollars.toFixed(2)}</span></span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <button onClick={() => setTipSplitDraft(prev => ({ ...prev, [id]: { mode: '$', val: prev[id]?.val || '' } }))} className={`py-2 rounded-lg text-sm font-semibold ${d.mode === '$' ? 'bg-purple-600 text-white' : 'bg-white/10 text-white/70'}`}>$ Amount</button>
                    <button onClick={() => setTipSplitDraft(prev => ({ ...prev, [id]: { mode: '%', val: (prev[id]?.val || '').replace('.', '') } }))} className={`py-2 rounded-lg text-sm font-semibold ${d.mode === '%' ? 'bg-purple-600 text-white' : 'bg-white/10 text-white/70'}`}>% Percent</button>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {['1','2','3','4','5','6','7','8','9'].map(n => (
                      <button key={n} onClick={() => append(n)} className="py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-semibold active:scale-95 transition-all">{n}</button>
                    ))}
                    <button onClick={() => append('.')} disabled={d.mode === '%'} className="py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-semibold disabled:opacity-30 active:scale-95 transition-all">.</button>
                    <button onClick={() => append('0')} className="py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-semibold active:scale-95 transition-all">0</button>
                    <button onClick={() => setVal(v => v.slice(0, -1))} className="py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white active:scale-95 transition-all flex items-center justify-center"><Delete className="w-5 h-5" /></button>
                  </div>
                  <button onClick={() => setActiveTipBarberId(null)} className="mt-2 w-full py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-purple-500 text-white font-bold active:scale-95 transition-all">Set</button>
                </div>
              );
            })() : (
              <>
                <div className="flex items-center justify-between border-t border-white/10 pt-3 mb-4">
                  <span className="text-base text-white/80">Total tip</span>
                  <span className="text-2xl font-extrabold text-emerald-300">
                    ${serviceBarbers.reduce((s, b) => { const d = tipSplitDraft[b.barberId] || { mode: '%' as const, val: '' }; return s + (d.mode === '%' ? ((parseFloat(d.val) || 0) / 100) * b.serviceTotal : (parseFloat(d.val) || 0)); }, 0).toFixed(2)}
                  </span>
                </div>
                <button
                  onClick={() => {
                    const split: Record<string, number> = {};
                    serviceBarbers.forEach(b => {
                      const d = tipSplitDraft[b.barberId] || { mode: '%' as const, val: '' };
                      split[b.barberId] = round2(d.mode === '%' ? ((parseFloat(d.val) || 0) / 100) * b.serviceTotal : (parseFloat(d.val) || 0));
                    });
                    setTipSplit(split);
                    setTipPercentage(0);
                    setCustomTip('');
                    setActiveTipBarberId(null);
                    setShowTipSplitModal(false);
                    // Go straight to checkout — same as the regular tip's "Check Out →" (Jose 2026-11-03).
                    // Defer one tick so the tipSplit state commits before handlePayment reads it.
                    setTimeout(() => handlePayment(), 0);
                  }}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-extrabold text-xl active:scale-[0.99] transition-transform touch-manipulation"
                >Check Out →</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mobile Payment Modal - Slide from right */}
      {showMobilePaymentModal && (
        <div
          className="fixed inset-0 z-[10001] bg-black/60"
          onClick={() => {
            setShowMobilePaymentModal(false);
            setPaymentLinkGenerated(null);
            setPaymentQRCode(null);
            setCustomerPhone('');
          }}
          style={{ backdropFilter: 'blur(4px)' }}
        >
          <div
            className="absolute top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'slideInRight 0.3s ease-out' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 text-white px-3 py-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5" />
                  <h2 className="text-sm font-bold">Send Payment Link</h2>
                </div>
                <button
                  onClick={() => {
                    setShowMobilePaymentModal(false);
                    setPaymentLinkGenerated(null);
                    setPaymentQRCode(null);
                    setCustomerPhone('');
                  }}
                  className="w-6 h-6 flex items-center justify-center text-white hover:bg-white/20 rounded transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-blue-100 text-xs mt-0.5">Customer can pay from their mobile device</p>
            </div>

            <div className="flex-1 overflow-y-auto p-3 scroll-fix">
              {/* COMPLETE SALE CARD (Jose 2026-10-31): the link modal must carry the whole sale — WHO it's
                  for (named client + contact), WHAT they're paying for (service · barber · date, every line
                  item), and the totals — so the sender sees a complete, named ticket, not a bare amount. */}
              <div className="mb-2 rounded-lg bg-gray-50 border border-gray-200 overflow-hidden">
                {/* Who */}
                <div className="px-3 py-2 bg-white border-b border-gray-100">
                  <div className="text-[10px] uppercase tracking-wide text-gray-400">Billing</div>
                  <div className="text-sm font-bold text-gray-900">{appointment.clientName || 'This client'}</div>
                  <div className="text-[11px] text-gray-500 flex flex-wrap gap-x-2">
                    {effClientPhone && <span>{effClientPhone}</span>}
                    {effClientEmail && <span>{effClientEmail}</span>}
                    {!effClientPhone && !effClientEmail && <span>No contact on file — enter a phone below</span>}
                  </div>
                </div>
                {/* What — the appointment context */}
                {appointment.barberName && (
                  <div className="px-3 py-2 border-b border-gray-100">
                    <div className="text-xs font-semibold text-gray-800">{serviceName}</div>
                    <div className="text-[11px] text-gray-500">with {appointment.barberName}</div>
                  </div>
                )}
                {/* Line items + totals */}
                <div className="px-3 py-2 space-y-1 text-xs">
                  <div className="flex justify-between text-gray-700"><span className="truncate pr-2">{serviceName}</span><span className="whitespace-nowrap">${displayServicePrice.toFixed(2)}</span></div>
                  {extraServicesBillable > 0 && (
                    <div className="flex justify-between text-gray-700"><span>Add-on services</span><span>${extraServicesBillable.toFixed(2)}</span></div>
                  )}
                  {additionalItems.map((item, i) => (
                    <div key={i} className="flex justify-between text-gray-700"><span className="truncate pr-2">{item.name}</span><span className="whitespace-nowrap">${item.price.toFixed(2)}</span></div>
                  ))}
                  {manualDiscount > 0 && (
                    <div className="flex justify-between text-rose-600"><span>Discount{discountReason ? ` · ${discountReason}` : ''}</span><span>-${manualDiscount.toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between text-gray-500 border-t border-gray-200 pt-1"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-gray-500"><span>Tax</span><span>${tax.toFixed(2)}</span></div>
                  <div className="flex justify-between text-gray-500"><span>Tip</span><span className="text-green-600">${tipAmount.toFixed(2)}</span></div>
                  <div className="flex justify-between border-t border-gray-200 pt-1 font-bold text-sm text-gray-900"><span>Total</span><span className="text-indigo-600">${total.toFixed(2)}</span></div>
                </div>
              </div>

              {/* Phone Number Input */}
              {!paymentLinkGenerated && (
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Customer Phone</label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-blue-500"
                    disabled={sendingPaymentLink}
                  />
                  <p className="text-[10px] text-white/60 mt-0.5">
                    SMS with secure payment link{customerPhone ? '' : ' — no number on file, enter one'}
                  </p>
                </div>
              )}

              {/* Payment Link Display (after generation) */}
              {paymentLinkGenerated && (
                <div className="space-y-6">
                  {/* Success Message */}
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <Check className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Payment link sent successfully!
                        </h3>
                        <p className="mt-2 text-sm text-green-700">
                          The customer will receive an SMS at {customerPhone} with a secure payment link.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* QR Code */}
                  {paymentQRCode && (
                    <div className="text-center">
                      <h3 className="font-semibold mb-3 flex items-center justify-center">
                        <QrCode className="w-5 h-5 mr-2" />
                        Or scan QR code to pay
                      </h3>
                      <div className="inline-block p-4 bg-white border-2 border-gray-200 rounded-lg">
                        <img
                          src={paymentQRCode}
                          alt="Payment QR Code"
                          className="w-64 h-64"
                        />
                      </div>
                      <p className="text-sm text-white/70 mt-3">
                        Customer can scan this with their phone camera
                      </p>
                    </div>
                  )}

                  {/* Copy Link */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Link
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={paymentLinkGenerated}
                        readOnly
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                      />
                      <button
                        onClick={handleCopyPaymentLink}
                        className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </button>
                    </div>
                    <p className="text-xs text-white/70 mt-2">
                      You can also share this link manually via email or other messaging apps
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3">
                {!paymentLinkGenerated ? (
                  <>
                    <button
                      onClick={() => {
                        setShowMobilePaymentModal(false);
                        setCustomerPhone('');
                      }}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendPaymentLink}
                      disabled={sendingPaymentLink || !customerPhone}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 to-indigo-700 text-white rounded-lg font-semibold hover:from-indigo-600 hover:to-indigo-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {sendingPaymentLink ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Payment Link
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setShowMobilePaymentModal(false);
                      setPaymentLinkGenerated(null);
                      setPaymentQRCode(null);
                      setCustomerPhone('');
                    }}
                    className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all flex items-center justify-center"
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Done
                  </button>
                )}
              </div>

              {/* Info Note */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> The payment link is powered by Stripe and is secure.
                  The customer will be redirected to a payment page where they can enter their card details.
                  You'll be notified when the payment is complete.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD GIFT CARD TO ACCOUNT (Jose 2026-11-01): after a gift card is redeemed, offer to save it to
          the client so it shows as a payment method on their appointment card next time. */}
      {pendingGcLink && (
        <div className="fixed inset-0 z-[10002] bg-black/60 flex items-center justify-center p-4" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-xs w-full p-5 text-center">
            <div className="text-base font-bold text-gray-900 mb-1">Add this gift card to the account?</div>
            <p className="text-sm text-gray-600 mb-4">Save it to <b>{pendingGcLink.clientName}</b> so it shows as a payment method on their appointment card next time.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPendingGcLink(null)}
                className="flex-1 py-2.5 rounded-xl font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                No
              </button>
              <button
                onClick={async () => {
                  const p = pendingGcLink; setPendingGcLink(null);
                  try { await giftCardService.linkToClient(p.giftCardId, p.clientId); toast.success('Gift card saved to the account ✓'); }
                  catch { toast.error('Could not save the gift card'); }
                }}
                className="flex-1 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#1c1a17] to-[#c0824a] shadow-button hover:opacity-90"
              >
                Yes, add it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
